import React, { useEffect, useMemo, useState } from 'react';
import ReactDOM from 'react-dom';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell, CartesianGrid } from 'recharts';
import {
  Calendar, ArrowClockwise, SignOut, Eye, Check, X as XIcon,
  Money, Plus, Trash, PencilSimple, Storefront, Package as PackageIcon,
  Users as UsersIcon, Palette, Lifebuoy, ArrowsClockwise, Truck, ChatCircleText, Flag, FileArrowDown,
  Ticket, ArrowsDownUp
} from '@phosphor-icons/react';
import API from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { useBranding } from '../../lib/branding';

const TABS = [
  { id: 'orders', label: 'Orders', icon: PackageIcon },
  { id: 'partners', label: 'Partners', icon: UsersIcon },
  { id: 'users', label: 'Users', icon: UsersIcon },
  { id: 'categories', label: 'Categories', icon: Storefront },
  { id: 'catalog', label: 'Catalog', icon: PackageIcon },
  { id: 'branding', label: 'Branding', icon: Palette },
  { id: 'coupons', label: 'Coupons', icon: Ticket },
  { id: 'cashouts', label: 'Cashouts', icon: Money },
  { id: 'support', label: 'Support', icon: Lifebuoy },
];

const STATUS_COLORS = {
  pending_payment: '#a3a3a3', pending_partner: '#e30066', unassigned: '#ef4444',
  accepted: '#00a3e0', printing: '#ffd700', printed: '#8b5cf6',
  out_for_delivery: '#f97316', delivered: '#10b981',
};

const KPI = ({ label, value, accent }) => (
  <div className="pk-card p-4" style={accent ? { background: accent } : {}}>
    <div className="text-[9px] tracking-[0.22em] uppercase text-neutral-600">{label}</div>
    <div className="font-display text-[26px] font-mono mt-1">{value}</div>
  </div>
);

const AdminDashboard = () => {
  const { user, logout } = useAuth();
  const { branding, refresh: refreshBranding } = useBranding();
  const nav = useNavigate();
  const [tab, setTab] = useState('orders');
  const [stats, setStats] = useState(null);
  const [customRange, setCustomRange] = useState(false);
  const [range, setRange] = useState({ start: '', end: '' });
  const [openTicketCount, setOpenTicketCount] = useState(0);
  const [newInquiryCount, setNewInquiryCount] = useState(0);

  const loadStats = async (params) => {
    try { const r = await API.get('/admin/stats', { params }); setStats(r.data); }
    catch (e) { toast.error('Failed to load stats'); }
  };

  const loadSupportBadges = async () => {
    try {
      const t = await API.get('/support/tickets');
      setOpenTicketCount((t.data || []).filter(x => x.status === 'open').length);
    } catch { /* ignore */ }
    try {
      const e = await API.get('/admin/enterprise', { params: { stage: 'new' } });
      setNewInquiryCount((e.data || []).length);
    } catch { /* ignore */ }
  };

  useEffect(() => {
    loadStats();
    loadSupportBadges();
    const iv = setInterval(loadSupportBadges, 20000);
    return () => clearInterval(iv);
  }, []);

  const applyRange = () => {
    if (!range.start || !range.end) { toast.error('Pick both dates'); return; }
    loadStats(range);
  };
  const resetRange = () => { setCustomRange(false); setRange({ start: '', end: '' }); loadStats(); };

  return (
    <div className="min-h-screen bg-neutral-100">
      <header className="bg-white border-b-2 border-black sticky top-0 z-40">
        <div className="max-w-[1500px] mx-auto px-6 h-16 flex items-center justify-between">
          <div>
            <div className="font-display text-[18px] leading-none">{branding.logo_text} · Admin</div>
            <div className="text-[9px] tracking-[0.22em] uppercase text-neutral-500 mt-1">{user?.email}</div>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => setCustomRange(v => !v)} className="pk-chip" data-testid="range-toggle"><Calendar size={12}/> Custom Range</button>
            <button onClick={resetRange} className="pk-chip" data-testid="range-reset"><ArrowClockwise size={12}/> Current Month</button>
            <button onClick={async () => { await logout(); nav('/'); }} className="pk-chip"><SignOut size={12}/> Logout</button>
          </div>
        </div>
      </header>

      <main className="max-w-[1500px] mx-auto p-6 space-y-6">
        {customRange && (
          <div className="pk-card p-4 flex items-end gap-3 flex-wrap">
            <div><label className="pk-label">From</label><input type="date" className="pk-input" value={range.start} onChange={e => setRange({ ...range, start: e.target.value })} data-testid="range-from"/></div>
            <div><label className="pk-label">To</label><input type="date" className="pk-input" value={range.end} onChange={e => setRange({ ...range, end: e.target.value })} data-testid="range-to"/></div>
            <button onClick={applyRange} className="pk-btn" data-testid="range-apply">Apply</button>
            <div className="text-[11px] font-mono ml-auto">{stats?.window_label}</div>
          </div>
        )}

        {/* KPI strip */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <KPI label={`Revenue (${stats.window_label})`} value={`₹${(stats.kpis.revenue || 0).toLocaleString('en-IN')}`} accent="var(--pk-yellow)" />
            <KPI label="Orders" value={stats.kpis.orders} />
            <KPI label="Paid" value={stats.kpis.paid} />
            <KPI label="Partners" value={stats.kpis.partners} />
            <KPI label="Open tickets" value={stats.kpis.open_tickets} />
          </div>
        )}

        {/* Charts */}
        {stats && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="pk-card p-4">
              <div className="text-[10px] tracking-[0.24em] uppercase font-bold mb-3">Orders by Status</div>
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={stats.status_data}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e5e5" />
                  <XAxis dataKey="status" tick={{ fontSize: 10 }} />
                  <YAxis tick={{ fontSize: 11 }} />
                  <Tooltip cursor={{ fill: '#fff2b1' }} />
                  <Bar dataKey="count">
                    {stats.status_data.map((d, i) => <Cell key={i} fill={STATUS_COLORS[d.status] || '#a3a3a3'} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
            <div className="pk-card p-4">
              <div className="text-[10px] tracking-[0.24em] uppercase font-bold mb-3">Daily Revenue</div>
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={stats.daily}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e5e5" />
                  <XAxis dataKey="date" tick={{ fontSize: 9 }} />
                  <YAxis tick={{ fontSize: 11 }} />
                  <Tooltip cursor={{ fill: '#fff2b1' }} formatter={(v) => `₹${v.toLocaleString('en-IN')}`} />
                  <Bar dataKey="revenue" fill="#e30066" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}

        {/* Tabs */}
        <div className="pk-card">
          <div className="flex overflow-x-auto overflow-y-visible border-b-2 border-black">
            {TABS.map(t => {
              const Icon = t.icon;
              const active = tab === t.id;
              return (
                <button key={t.id} onClick={() => setTab(t.id)} className={`px-4 py-3 flex items-center gap-2 text-[11px] tracking-[0.16em] uppercase font-bold border-r-2 border-black whitespace-nowrap relative ${active ? 'text-white' : 'bg-white'}`}
                  style={{ background: active ? 'var(--pk-primary)' : '#fff' }} data-testid={`tab-${t.id}`}>
                  <Icon size={14} weight={active ? 'fill' : 'regular'} /> {t.label}
                  {t.id === 'support' && (openTicketCount + newInquiryCount) > 0 && (
                    <span
                      className="absolute -top-2 -right-2 flex items-center gap-0.5 px-1.5 h-5 border-2 border-black text-white text-[10px] font-bold font-mono leading-none"
                      style={{ background: 'var(--pk-primary)' }}
                      data-testid="support-tab-badge"
                      title={`${openTicketCount} open ticket(s) · ${newInquiryCount} new inquiry(ies)`}
                    >
                      <Flag size={9} weight="fill" />
                      {openTicketCount + newInquiryCount}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
          <div className="p-4">
            {tab === 'orders' && <OrdersTab onReload={loadStats} />}
            {tab === 'partners' && <PartnersTab />}
            {tab === 'users' && <UsersTab />}
            {tab === 'categories' && <CategoriesTab />}
            {tab === 'catalog' && <CatalogTab />}
            {tab === 'branding' && <BrandingTab onSaved={refreshBranding} />}
            {tab === 'coupons' && <CouponsTab />}
            {tab === 'cashouts' && <CashoutsTab />}
            {tab === 'support' && <SupportTab />}
          </div>
        </div>
      </main>
    </div>
  );
};

// ------------- Orders Tab -------------
const OrdersTab = () => {
  const [orders, setOrders] = useState([]);
  const [q, setQ] = useState('');
  const [range, setRange] = useState({ start: '', end: '' });
  const [statusFilter, setStatusFilter] = useState('');
  const [selected, setSelected] = useState(null);
  const load = async () => { try { const r = await API.get('/admin/orders'); setOrders(r.data); } catch (e) { console.error('[admin] load orders failed', e); } };
  useEffect(() => { load(); }, []);
  const reassign = async (oid) => { try { const r = await API.post(`/admin/orders/${oid}/reassign`); toast.success(r.data.new_partner_id ? 'Reassigned' : 'No partner available'); load(); } catch (e) { toast.error('Failed'); } };
  const forceDeliver = async (oid) => { if (!window.confirm('Force mark as delivered?')) return; try { await API.post(`/admin/orders/${oid}/force-deliver`); toast.success('Marked delivered'); load(); } catch (e) { toast.error('Failed'); } };

  const filtered = useMemo(() => {
    return orders.filter(o => {
      if (statusFilter && o.status !== statusFilter) return false;
      if (range.start) { const d = (o.created_at || '').slice(0, 10); if (d < range.start) return false; }
      if (range.end) { const d = (o.created_at || '').slice(0, 10); if (d > range.end) return false; }
      if (q) {
        const s = q.toLowerCase();
        return (o.order_id?.toLowerCase().includes(s)
          || o.customer_name?.toLowerCase().includes(s)
          || o.delivery_phone?.includes(q)
          || o.delivery_pincode?.includes(q)
          || o.items?.some(i => i.product_name?.toLowerCase().includes(s)));
      }
      return true;
    });
  }, [orders, q, range, statusFilter]);

  const statuses = ['pending_payment','pending_partner','unassigned','accepted','printing','printed','out_for_delivery','delivered'];

  return (
    <div>
      <div className="flex flex-wrap items-end gap-2 mb-3">
        <div className="flex-1 min-w-[200px]">
          <label className="pk-label">Search (order id, name, phone, pincode, product)</label>
          <input value={q} onChange={e => setQ(e.target.value)} className="pk-input" placeholder="Type to search…" data-testid="admin-orders-search" />
        </div>
        <div><label className="pk-label">From</label><input type="date" className="pk-input" value={range.start} onChange={e => setRange({ ...range, start: e.target.value })} data-testid="admin-orders-from" /></div>
        <div><label className="pk-label">To</label><input type="date" className="pk-input" value={range.end} onChange={e => setRange({ ...range, end: e.target.value })} data-testid="admin-orders-to" /></div>
        <div>
          <label className="pk-label">Status</label>
          <select className="pk-select" value={statusFilter} onChange={e => setStatusFilter(e.target.value)} data-testid="admin-orders-status">
            <option value="">All</option>
            {statuses.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
        {(q || range.start || range.end || statusFilter) && (
          <button onClick={() => { setQ(''); setRange({ start: '', end: '' }); setStatusFilter(''); }} className="pk-chip">Clear</button>
        )}
        <div className="text-[10px] tracking-widest uppercase text-neutral-500 ml-auto">Showing {filtered.length} / {orders.length}</div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-[12px]">
          <thead className="bg-neutral-100">
            <tr className="text-left"><th className="p-2 text-[9px] tracking-widest uppercase">Order</th><th className="p-2 text-[9px] tracking-widest uppercase">Customer</th><th className="p-2 text-[9px] tracking-widest uppercase">Items</th><th className="p-2 text-[9px] tracking-widest uppercase">Partner</th><th className="p-2 text-[9px] tracking-widest uppercase">Status</th><th className="p-2 text-[9px] tracking-widest uppercase">Placed</th><th className="p-2 text-[9px] tracking-widest uppercase text-right">Total</th><th className="p-2 text-[9px] tracking-widest uppercase text-right">Actions</th></tr>
          </thead>
          <tbody>
            {filtered.map(o => (
              <tr key={o.order_id} className="border-t border-neutral-200 hover:bg-yellow-50 cursor-pointer" onClick={() => setSelected(o)} data-testid={`order-row-${o.order_id}`}>
                <td className="p-2 font-mono text-[10px] uppercase">#{o.order_id.slice(-10)}</td>
                <td className="p-2">{o.customer_name}<div className="text-[10px] text-neutral-500 font-mono">PIN {o.delivery_pincode}</div></td>
                <td className="p-2">{o.items.length} × items</td>
                <td className="p-2 font-mono text-[10px]">{o.partner_name ? <span className="font-sans text-[11px] font-bold">{o.partner_name}</span> : <span className="text-red-600">Unassigned</span>}</td>
                <td className="p-2"><span className={`status-${o.status} border border-black text-[9px] tracking-widest uppercase px-1.5 py-0.5 font-bold`}>{o.status}</span></td>
                <td className="p-2 font-mono text-[10px]">{o.created_at?.slice(0,16).replace('T',' ')}</td>
                <td className="p-2 text-right font-mono">₹{(o.total_amount || 0).toLocaleString('en-IN')}</td>
                <td className="p-2 text-right whitespace-nowrap" onClick={e => e.stopPropagation()}>
                  {o.status !== 'delivered' && <button onClick={() => reassign(o.order_id)} className="pk-chip text-[9px]" data-testid={`reassign-${o.order_id}`}><ArrowsClockwise size={10}/> Reassign</button>}
                  {o.status !== 'delivered' && <button onClick={() => forceDeliver(o.order_id)} className="pk-chip text-[9px] ml-1" data-testid={`force-${o.order_id}`}><Truck size={10}/> Force</button>}
                </td>
              </tr>
            ))}
            {filtered.length === 0 && <tr><td colSpan={8} className="p-6 text-center text-neutral-500">No matching orders.</td></tr>}
          </tbody>
        </table>
      </div>
      {selected && <OrderDetailModal order={selected} onClose={() => setSelected(null)} />}
    </div>
  );
};

const OrderDetailModal = ({ order, onClose }) => {
  const [messages, setMessages] = useState([]);
  const [partner, setPartner] = useState(null);
  useEffect(() => {
    // Admin can read via /orders/{id}/messages? Only customer/partner have access.
    // Instead pull from a dedicated admin path — we don't have one, use direct call and fallback.
    (async () => {
      try { const r = await API.get(`/orders/${order.order_id}/messages`); setMessages(Array.isArray(r.data) ? r.data : (r.data.messages || [])); } catch { /* admin not gated in this endpoint - graceful */ }
      if (order.partner_id) {
        try { const p = await API.get(`/admin/partners/${order.partner_id}`); setPartner(p.data); } catch (e) { console.error('[admin] load partner failed', e); }
      }
    })();
  }, [order.order_id, order.partner_id]);
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white pk-border-thick brutal-shadow-lg max-w-3xl w-full max-h-[90vh] overflow-y-auto p-6 slide-up" onClick={e => e.stopPropagation()}>
        <div className="flex items-start justify-between">
          <div>
            <div className="font-mono text-[10px] tracking-widest uppercase text-neutral-500">#{order.order_id.slice(-12)}</div>
            <div className="font-display text-[22px] mt-1">Order details</div>
          </div>
          <button onClick={onClose} className="pk-chip"><XIcon size={12}/></button>
        </div>

        <div className="mt-4 grid grid-cols-2 gap-4">
          <div className="pk-card p-3">
            <div className="pk-label">Customer</div>
            <div className="font-bold text-[13px]">{order.customer_name}</div>
            <div className="text-[11px] font-mono text-neutral-600 mt-1">{order.delivery_phone || '(hidden)'}</div>
            <div className="text-[11px] mt-1">{order.delivery_address}</div>
            <div className="text-[11px] font-mono">PIN {order.delivery_pincode}</div>
            {order.delivery_map_link && <a href={order.delivery_map_link} target="_blank" rel="noreferrer" className="text-[10px] underline">Open in Maps →</a>}
          </div>
          <div className="pk-card p-3">
            <div className="pk-label">Partner</div>
            {partner ? (
              <>
                <div className="font-bold text-[13px]">{partner.shop_name || partner.name}</div>
                <div className="text-[11px] font-mono text-neutral-600 mt-1">{partner.email}</div>
                <div className="text-[11px] font-mono">{partner.phone}</div>
                <div className="text-[10px] text-neutral-500 mt-1">Accepted: {order.history?.find(h => h.status === 'accepted')?.at?.slice(0,16).replace('T',' ') || '—'}</div>
              </>
            ) : <div className="text-[12px] text-neutral-500">Not yet assigned</div>}
          </div>
        </div>

        <div className="mt-4">
          <div className="pk-label">Items & artwork files</div>
          <div className="space-y-2">
            {order.items.map((i, idx) => (
              <div key={idx} className="pk-card p-3 flex items-center gap-3">
                <div className="flex-1">
                  <div className="font-bold text-[13px]">{i.quantity} × {i.product_name}</div>
                  <div className="text-[10px] text-neutral-500 font-mono">{i.material} · {i.size || (i.width ? `${i.width}×${i.height}ft` : '')} · {i.color_profile || 'no CMYK info'}</div>
                </div>
                {i.file_id && <a href={`${BACKEND_URL}/api/files/${i.file_id}/download`} target="_blank" rel="noreferrer" className="pk-chip active text-[9px]" data-testid={`admin-view-file-${idx}`}>View file</a>}
                {i.file_link && <a href={i.file_link} target="_blank" rel="noreferrer" className="pk-chip active text-[9px]">External link</a>}
                <div className="font-mono font-bold">₹{(i.total || 0).toLocaleString('en-IN')}</div>
              </div>
            ))}
          </div>
        </div>

        <div className="mt-4">
          <div className="pk-label">Timeline</div>
          <div className="text-[11px] font-mono space-y-1">
            {(order.history || []).map((h, i) => (
              <div key={i} className="flex gap-2"><span className={`status-${h.status} px-1.5 py-0.5 border border-black text-[9px] uppercase tracking-widest font-bold`}>{h.status}</span><span className="text-neutral-500">{h.at?.slice(0,16).replace('T',' ')}</span>{h.by && <span className="text-neutral-400">by {h.by}</span>}{h.note && <span className="text-neutral-400 italic">— {h.note}</span>}</div>
            ))}
          </div>
        </div>

        <div className="mt-4">
          <div className="pk-label">Chat ({messages.length})</div>
          <div className="pk-border p-3 space-y-2 max-h-64 overflow-y-auto bg-neutral-50">
            {messages.length === 0 && <div className="text-[12px] text-center text-neutral-400 py-4">No messages exchanged.</div>}
            {messages.map(m => (
              <div key={m.message_id} className="text-[12px]">
                <span className="text-[9px] uppercase tracking-widest font-bold text-neutral-500">{m.sender_role} · {m.created_at?.slice(11,16)}</span>
                <div className="pk-border-thick border-2 bg-white p-2 mt-1">
                  {m.file_id && <a href={`${BACKEND_URL}/api/files/${m.file_id}/download`} target="_blank" rel="noreferrer"><img src={`${BACKEND_URL}/api/files/${m.file_id}/download`} alt="" className="max-h-32 border border-black" /></a>}
                  {m.text && <div>{m.text}</div>}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="mt-4 flex items-center justify-between">
          <div className="text-[11px] uppercase tracking-widest">Total <b className="font-mono">₹{(order.total_amount || 0).toLocaleString('en-IN')}</b></div>
          <div className="text-[11px] uppercase tracking-widest">Payment <b>{order.payment_status}</b></div>
        </div>
      </div>
    </div>
  );
};

// ------------- Partners Tab -------------
const PartnersTab = () => {
  const [partners, setPartners] = useState([]);
  const [cats, setCats] = useState([]);
  const [selected, setSelected] = useState(null);
  const load = async () => { const r = await API.get('/partners'); setPartners(r.data); const c = await API.get('/categories'); setCats(c.data); };
  useEffect(() => { load(); }, []);
  const setStatus = async (pid, status) => { try { await API.post(`/admin/partners/${pid}/status`, { status }); toast.success('Updated'); load(); if (selected) setSelected({ ...selected, partner_status: status }); } catch { toast.error('Failed'); } };
  const setCategories = async (pid, cats) => { try { await API.post(`/admin/partners/${pid}/categories`, { allowed_categories: cats }); toast.success('Categories updated'); load(); if (selected) setSelected({ ...selected, allowed_categories: cats }); } catch { toast.error('Failed'); } };
  return (
    <div>
      <table className="w-full text-[12px]">
        <thead className="bg-neutral-100">
          <tr className="text-left"><th className="p-2 text-[9px] tracking-widest uppercase">Name</th><th className="p-2 text-[9px] tracking-widest uppercase">Contact</th><th className="p-2 text-[9px] tracking-widest uppercase">Pincodes</th><th className="p-2 text-[9px] tracking-widest uppercase">Rating</th><th className="p-2 text-[9px] tracking-widest uppercase">Online</th><th className="p-2 text-[9px] tracking-widest uppercase">Status</th><th className="p-2 text-[9px] tracking-widest uppercase text-right">Actions</th></tr>
        </thead>
        <tbody>
          {partners.map(p => (
            <tr key={p.user_id} className="border-t border-neutral-200 hover:bg-yellow-50">
              <td className="p-2 font-bold">{p.shop_name || p.name}<div className="text-[10px] text-neutral-500 font-mono">{p.email}</div></td>
              <td className="p-2 font-mono text-[11px]">{p.phone || '—'}</td>
              <td className="p-2 font-mono text-[11px]">{(p.service_pincodes || []).join(', ') || '—'}</td>
              <td className="p-2" data-testid={`partner-rating-${p.user_id}`}>
                {p.rating_count ? (
                  <div className="flex items-center gap-1 text-[11px]">
                    <span className="font-bold" style={{ color: '#f59e0b' }}>★ {p.rating_avg?.toFixed(1)}</span>
                    <span className="text-neutral-400 font-mono">&nbsp;({p.rating_count})</span>
                  </div>
                ) : (
                  <span className="text-[10px] tracking-widest uppercase text-neutral-400">—</span>
                )}
              </td>
              <td className="p-2">
                <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest">
                  <span className="w-2 h-2 rounded-full" style={{ background: p.is_online === false ? '#ef4444' : '#10b981' }} />
                  {p.is_online === false ? 'Offline' : 'Online'}
                </span>
              </td>
              <td className="p-2"><span className={`border border-black text-[9px] tracking-widest uppercase px-1.5 py-0.5 font-bold ${p.partner_status === 'approved' ? 'status-delivered' : p.partner_status === 'rejected' ? 'status-out_for_delivery' : 'status-pending_partner'}`}>{p.partner_status || 'pending_review'}</span></td>
              <td className="p-2 text-right"><button onClick={() => setSelected(p)} className="pk-chip text-[9px]" data-testid={`view-partner-${p.user_id}`}><Eye size={10}/> View / Edit</button></td>
            </tr>
          ))}
        </tbody>
      </table>

      {selected && <PartnerDetailModal partner={selected} onClose={() => setSelected(null)} onStatus={(s) => setStatus(selected.user_id, s)} onCats={(c) => setCategories(selected.user_id, c)} cats={cats} />}
    </div>
  );
};

const PartnerDetailModal = ({ partner, onClose, onStatus, onCats, cats }) => {
  const [ledger, setLedger] = useState(null);
  const [orders, setOrders] = useState([]);
  const [orderSel, setOrderSel] = useState(null);
  const [editing, setEditing] = useState(false);
  const [showDelete, setShowDelete] = useState(false);
  const [deleteText, setDeleteText] = useState('');
  const [edit, setEdit] = useState({
    shop_name: partner.shop_name || '',
    phone: partner.phone || '',
    address: partner.address || '',
    service_pincodes: (partner.service_pincodes || []).join(', '),
    machine_brand: partner.machine_brand || '',
    bank_account: partner.bank_details?.account || '',
    bank_ifsc: partner.bank_details?.ifsc || '',
    upi_id: partner.upi_id || '',
    gst: partner.gst || '',
    platform_margin_pct: partner.platform_margin_pct ?? 10,
  });
  useEffect(() => {
    (async () => {
      try { const l = await API.get(`/admin/partners/${partner.user_id}/ledger`); setLedger(l.data); } catch (e) { console.error('[admin] load partner ledger failed', e); }
      try {
        const r = await API.get('/admin/orders');
        setOrders((r.data || []).filter(o => o.partner_id === partner.user_id));
      } catch (e) { console.error('[admin] load partner orders failed', e); }
    })();
  }, [partner.user_id]);
  const saveEdit = async () => {
    try {
      const marginNum = Number(edit.platform_margin_pct);
      const payload = {
        ...edit,
        service_pincodes: edit.service_pincodes.split(',').map(s => s.trim()).filter(Boolean),
        platform_margin_pct: isNaN(marginNum) ? 10 : Math.max(0, Math.min(100, marginNum)),
      };
      await API.put(`/admin/partners/${partner.user_id}`, payload);
      toast.success('Partner updated');
      setEditing(false);
      // Reflect locally
      Object.assign(partner, {
        shop_name: edit.shop_name, phone: edit.phone, address: edit.address,
        service_pincodes: payload.service_pincodes, machine_brand: edit.machine_brand,
        upi_id: edit.upi_id, gst: edit.gst,
        platform_margin_pct: payload.platform_margin_pct,
        bank_details: { account: edit.bank_account, ifsc: edit.bank_ifsc },
      });
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white pk-border-thick brutal-shadow-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto p-6 slide-up" onClick={e => e.stopPropagation()}>
        <div className="flex items-start justify-between">
          <div className="flex gap-3 items-start">
            {partner.machine_photo_file_id && (
              <a href={`${process.env.REACT_APP_BACKEND_URL}/api/files/${partner.machine_photo_file_id}/download`} target="_blank" rel="noreferrer" title="View machine photo">
                <img src={`${process.env.REACT_APP_BACKEND_URL}/api/files/${partner.machine_photo_file_id}/download`}
                     alt="Machine verification"
                     className="w-16 h-16 object-cover border-2 border-black brutal-shadow-sm" />
              </a>
            )}
            <div>
              <div className="font-display text-[22px]">{partner.shop_name || partner.name}</div>
              <div className="text-[11px] text-neutral-500">{partner.email} · <span className="inline-flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full" style={{ background: partner.is_online === false ? '#ef4444' : '#10b981' }}/>{partner.is_online === false ? 'Offline' : 'Online'}</span></div>
              {partner.machine_photo_file_id && <div className="text-[10px] text-green-700 mt-1">✓ Machine verified · tap photo to enlarge</div>}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => setEditing(v => !v)} className="pk-chip" data-testid="edit-partner-toggle"><PencilSimple size={12}/> {editing ? 'Cancel edit' : 'Edit'}</button>
            {partner.partner_status === 'rejected' && (
              <button onClick={() => setShowDelete(true)} className="pk-chip" style={{ background: '#ef4444', color: '#fff' }} data-testid="delete-partner-toggle"><Trash size={12}/> Delete</button>
            )}
            <button onClick={onClose} className="pk-chip"><XIcon size={12}/></button>
          </div>
        </div>

        {showDelete && (
          <div className="mt-4 pk-card p-4" style={{ background: '#fef2f2', borderColor: '#ef4444' }}>
            <div className="font-bold text-[14px]" style={{ color: '#ef4444' }}>Permanent delete</div>
            <div className="text-[11px] text-neutral-700 mt-1">This will remove the partner and all their access forever. To confirm, type <b>complete DELETE</b> below.</div>
            <input className="pk-input font-mono mt-3" placeholder="Type: complete DELETE" value={deleteText} onChange={e => setDeleteText(e.target.value)} data-testid="delete-confirm-input" />
            <div className="mt-2 flex gap-2">
              <button onClick={() => { setShowDelete(false); setDeleteText(''); }} className="pk-btn pk-btn-outline flex-1">Cancel</button>
              <button
                disabled={deleteText !== 'complete DELETE'}
                onClick={async () => {
                  try {
                    await API.delete(`/admin/partners/${partner.user_id}`, { data: { confirm: 'complete DELETE' } });
                    toast.success('Partner deleted');
                    onClose();
                  } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
                }}
                className="pk-btn flex-1"
                style={{ background: deleteText === 'complete DELETE' ? '#ef4444' : '#a3a3a3', borderColor: '#0a0a0a' }}
                data-testid="delete-confirm-btn"
              >Delete forever</button>
            </div>
          </div>
        )}

        {editing ? (
          <div className="mt-4 grid grid-cols-2 gap-3">
            <div><label className="pk-label">Shop name</label><input className="pk-input" value={edit.shop_name} onChange={e => setEdit({ ...edit, shop_name: e.target.value })} data-testid="edit-shop-name"/></div>
            <div><label className="pk-label">Phone</label><input className="pk-input font-mono" value={edit.phone} onChange={e => setEdit({ ...edit, phone: e.target.value })}/></div>
            <div className="col-span-2"><label className="pk-label">Address</label><textarea rows={2} className="pk-textarea" value={edit.address} onChange={e => setEdit({ ...edit, address: e.target.value })}/></div>
            <div className="col-span-2"><label className="pk-label">Service pincodes (comma separated)</label><input className="pk-input font-mono" value={edit.service_pincodes} onChange={e => setEdit({ ...edit, service_pincodes: e.target.value })} data-testid="edit-pincodes"/></div>
            <div><label className="pk-label">Machine brand</label><input className="pk-input" value={edit.machine_brand} onChange={e => setEdit({ ...edit, machine_brand: e.target.value })}/></div>
            <div><label className="pk-label">UPI</label><input className="pk-input font-mono" value={edit.upi_id} onChange={e => setEdit({ ...edit, upi_id: e.target.value })}/></div>
            <div><label className="pk-label">Bank account</label><input className="pk-input font-mono" value={edit.bank_account} onChange={e => setEdit({ ...edit, bank_account: e.target.value })}/></div>
            <div><label className="pk-label">IFSC</label><input className="pk-input font-mono" value={edit.bank_ifsc} onChange={e => setEdit({ ...edit, bank_ifsc: e.target.value })}/></div>
            <div><label className="pk-label">GST</label><input className="pk-input font-mono" value={edit.gst} onChange={e => setEdit({ ...edit, gst: e.target.value })}/></div>
            <div className="col-span-2 pk-card p-3" style={{ borderColor: 'var(--pk-primary)' }}>
              <label className="pk-label flex items-center justify-between">
                <span>Platform margin % <span className="text-neutral-400 font-normal normal-case tracking-normal">(admin-only, hidden from partner)</span></span>
                <span className="font-mono text-[13px] font-bold" style={{ color: 'var(--pk-primary)' }}>{edit.platform_margin_pct}%</span>
              </label>
              <input
                type="range" min="0" max="40" step="0.5"
                className="w-full"
                value={edit.platform_margin_pct}
                onChange={e => setEdit({ ...edit, platform_margin_pct: Number(e.target.value) })}
                data-testid="edit-platform-margin"
              />
              <p className="text-[10px] text-neutral-500 mt-1 leading-snug">
                Partner sees <b>₹{Math.round((1 - Number(edit.platform_margin_pct) / 100) * 1000)}</b> on every ₹1,000 order and in all their earnings. Set to 0 for a flat partner agreement.
              </p>
            </div>
            <div className="col-span-2 flex gap-2">
              <button onClick={() => setEditing(false)} className="pk-btn pk-btn-outline flex-1">Cancel</button>
              <button onClick={saveEdit} className="pk-btn pk-btn-magenta flex-1" data-testid="save-partner-edit">Save changes</button>
            </div>
          </div>
        ) : (
          <div className="mt-4 grid grid-cols-2 gap-4 text-[12px]">
            <div><div className="pk-label">Phone</div><span className="font-mono">{partner.phone || '—'}</span></div>
            <div><div className="pk-label">Address</div>{partner.address || '—'}</div>
            <div><div className="pk-label">Machine brand</div>{partner.machine_brand || '—'}</div>
            <div><div className="pk-label">Machines</div><div className="font-mono">{(partner.machine_types || []).join(', ') || '—'}</div></div>
            <div><div className="pk-label">Pincodes</div><div className="font-mono">{(partner.service_pincodes || []).join(', ') || '—'}</div></div>
            <div><div className="pk-label">Bank</div><div className="font-mono">{partner.bank_details?.account || '—'} / {partner.bank_details?.ifsc || '—'}</div></div>
            <div><div className="pk-label">UPI</div><div className="font-mono">{partner.upi_id || '—'}</div></div>
            <div><div className="pk-label">GST</div><div className="font-mono">{partner.gst || '—'}</div></div>
            <div className="col-span-2 pk-card p-2 flex items-center justify-between" style={{ borderColor: 'var(--pk-primary)' }}>
              <div>
                <div className="pk-label mb-0">Platform margin</div>
                <div className="text-[9px] uppercase tracking-widest text-neutral-500">admin-only · hidden from partner</div>
              </div>
              <div className="font-stat text-[22px]" style={{ color: 'var(--pk-primary)' }}>
                {partner.platform_margin_pct ?? 10}%
              </div>
            </div>
          </div>
        )}

        {ledger && (
          <div className="mt-4">
            <div className="pk-label">Earnings summary</div>
            <div className="grid grid-cols-4 gap-2">
              <div className="pk-card p-2"><div className="text-[9px] tracking-widest uppercase text-neutral-500">Lifetime</div><div className="font-stat text-[18px]">₹{ledger.lifetime.toLocaleString('en-IN')}</div></div>
              <div className="pk-card p-2"><div className="text-[9px] tracking-widest uppercase text-neutral-500">Settled</div><div className="font-stat text-[18px]">₹{ledger.settled.toLocaleString('en-IN')}</div></div>
              <div className="pk-card p-2"><div className="text-[9px] tracking-widest uppercase text-neutral-500">Pending</div><div className="font-stat text-[18px]">₹{ledger.pending.toLocaleString('en-IN')}</div></div>
              <div className="pk-card p-2" style={{ background: 'var(--pk-yellow)' }}><div className="text-[9px] tracking-widest uppercase">Remaining</div><div className="font-stat text-[18px]">₹{ledger.available.toLocaleString('en-IN')}</div></div>
            </div>
          </div>
        )}

        <div className="mt-4">
          <div className="pk-label">Allowed categories</div>
          <div className="flex flex-wrap gap-2">
            {cats.map(c => {
              const on = (partner.allowed_categories || []).includes(c.category_id);
              return (
                <button key={c.category_id} onClick={() => {
                  const cur = new Set(partner.allowed_categories || []);
                  if (on) cur.delete(c.category_id); else cur.add(c.category_id);
                  onCats(Array.from(cur));
                }} className={`pk-chip ${on ? 'active' : ''}`}>{c.name}</button>
              );
            })}
          </div>
        </div>

        <div className="mt-4">
          <div className="pk-label">Status</div>
          <div className="flex gap-2 flex-wrap">
            {['approved', 'on_hold', 'rejected', 'pending_review'].map(s => (
              <button key={s} onClick={() => onStatus(s)} className={`pk-chip ${partner.partner_status === s ? 'active' : ''}`} data-testid={`partner-status-${s}`}>{s}</button>
            ))}
          </div>
        </div>

        <div className="mt-4">
          <div className="pk-label">Order history ({orders.length})</div>
          <div className="pk-border max-h-64 overflow-y-auto">
            <table className="w-full text-[11px]">
              <thead className="bg-neutral-100 sticky top-0"><tr><th className="p-2 text-left">Order</th><th className="p-2 text-left">Customer</th><th className="p-2 text-left">Status</th><th className="p-2 text-left">Placed</th><th className="p-2 text-right">Total</th></tr></thead>
              <tbody>
                {orders.map(o => (
                  <tr key={o.order_id} className="border-t border-neutral-200 hover:bg-yellow-50 cursor-pointer" onClick={() => setOrderSel(o)} data-testid={`partner-order-${o.order_id}`}>
                    <td className="p-2 font-mono text-[10px] uppercase">#{o.order_id.slice(-8)}</td>
                    <td className="p-2">{o.customer_name}</td>
                    <td className="p-2"><span className={`status-${o.status} border border-black text-[9px] tracking-widest uppercase px-1 py-0.5 font-bold`}>{o.status}</span></td>
                    <td className="p-2 font-mono text-[10px]">{o.created_at?.slice(0, 10)}</td>
                    <td className="p-2 text-right font-mono">₹{(o.total_amount || 0).toLocaleString('en-IN')}</td>
                  </tr>
                ))}
                {orders.length === 0 && <tr><td colSpan={5} className="p-4 text-center text-neutral-500">No orders yet.</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      </div>
      {orderSel && <OrderDetailModal order={orderSel} onClose={() => setOrderSel(null)} />}
    </div>
  );
};

// ------------- Categories Tab -------------
const CategoriesTab = () => {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ name: '', slug: '', description: '', image: '', order: 0 });
  const [editingId, setEditingId] = useState(null);
  const load = async () => { const r = await API.get('/categories'); setItems(r.data); };
  useEffect(() => { load(); }, []);
  const reset = () => { setForm({ name: '', slug: '', description: '', image: '', order: 0 }); setEditingId(null); };
  const submit = async (e) => {
    e.preventDefault();
    try {
      if (editingId) {
        await API.put(`/admin/categories/${editingId}`, form);
        toast.success('Updated');
      } else {
        await API.post('/admin/categories', form);
        toast.success('Added');
      }
      reset(); load();
    } catch { toast.error('Failed'); }
  };
  const startEdit = (c) => { setForm({ name: c.name, slug: c.slug, description: c.description || '', image: c.image || '', order: c.order || 0 }); setEditingId(c.category_id); };
  const del = async (id) => { if (!window.confirm('Delete?')) return; await API.delete(`/admin/categories/${id}`); load(); };
  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
      <div className="md:col-span-2 space-y-2">
        {items.map(c => (
          <div key={c.category_id} className={`pk-card p-3 flex items-center gap-3 ${editingId === c.category_id ? 'brutal-shadow-magenta' : ''}`}>
            <div className="w-14 h-14 bg-neutral-100 border border-black overflow-hidden">{c.image && <img src={c.image} alt="" className="w-full h-full object-cover"/>}</div>
            <div className="flex-1"><div className="font-bold text-[13px]">{c.name}</div><div className="text-[11px] text-neutral-500">{c.slug}</div><div className="text-[10px] text-neutral-400 mt-0.5">{c.description}</div></div>
            <button onClick={() => startEdit(c)} className="pk-chip text-[9px]" data-testid={`edit-cat-${c.slug}`}><PencilSimple size={10}/> Edit</button>
            <button onClick={() => del(c.category_id)} className="pk-chip text-[9px]" data-testid={`del-cat-${c.slug}`}><Trash size={10}/></button>
          </div>
        ))}
      </div>
      <form onSubmit={submit} className="pk-card p-4 space-y-2 self-start">
        <div className="font-display text-[16px]">{editingId ? 'Edit category' : 'Add category'}</div>
        <div><label className="pk-label">Name</label><input required className="pk-input" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} data-testid="cat-name"/></div>
        <div><label className="pk-label">Slug</label><input required className="pk-input font-mono" value={form.slug} onChange={e => setForm({ ...form, slug: e.target.value })} data-testid="cat-slug"/></div>
        <div>
          <label className="pk-label">Image (upload — max 2MB)</label>
          <input
            type="file"
            accept="image/*"
            onChange={async (e) => {
              const f = e.target.files?.[0];
              if (!f) return;
              if (f.size > 2 * 1024 * 1024) { toast.error('Image must be ≤2MB'); return; }
              const dataUrl = await new Promise((res) => { const r = new FileReader(); r.onload = () => res(r.result); r.readAsDataURL(f); });
              setForm({ ...form, image: dataUrl });
            }}
            data-testid="cat-image-upload"
          />
          {form.image && (
            <div className="mt-2 flex items-center gap-2">
              <img src={form.image} alt="" className="w-14 h-14 border-2 border-black object-cover" />
              <button type="button" onClick={() => setForm({ ...form, image: '' })} className="pk-chip text-[9px]"><Trash size={10}/> Remove</button>
            </div>
          )}
        </div>
        <div><label className="pk-label">Description</label><textarea rows={2} className="pk-textarea" value={form.description} onChange={e => setForm({ ...form, description: e.target.value })}/></div>
        <div className="flex gap-2">
          {editingId && <button type="button" onClick={reset} className="pk-btn pk-btn-outline flex-1">Cancel</button>}
          <button className="pk-btn flex-1" data-testid="cat-save">{editingId ? 'Save' : <><Plus size={14}/> Add</>}</button>
        </div>
      </form>
    </div>
  );
};

// ------------- Catalog Tab -------------
const CatalogTab = () => {
  const [items, setItems] = useState([]);
  const [cats, setCats] = useState([]);
  const [editing, setEditing] = useState(null); // full product object being edited
  const [catFilter, setCatFilter] = useState('');  // '' = all categories
  const [dragId, setDragId] = useState(null);
  const load = async () => { const r = await API.get('/catalog'); setItems(r.data); const c = await API.get('/categories'); setCats(c.data); };
  useEffect(() => { load(); }, []);
  const del = async (id) => { if (!window.confirm('Delete?')) return; await API.delete(`/admin/catalog/${id}`); load(); };
  const newProduct = () => setEditing({
    product_id: null, name: '', category_id: cats[0]?.category_id, pricing_mode: 'per_unit',
    base_price: 0, image: '', images: [], description: '', min_qty: 1,
    variant_groups: [],
    material_options: [], lamination_options: [], size_options: [],
    required_machine_types: [], has_lamination: false, lamination_surcharge: 0,
  });

  // Drag-reorder within a category. We only reorder the subset the user is viewing (one category)
  // then POST the new product_id list to persist sort_order on each product.
  const onDragStart = (pid) => (e) => {
    setDragId(pid);
    e.dataTransfer.effectAllowed = 'move';
    try { e.dataTransfer.setData('text/plain', pid); } catch { /* FF quirk */ }
  };
  const onDragOver = (e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; };
  const onDrop = (targetPid, targetCat) => async (e) => {
    e.preventDefault();
    const sourceId = dragId;
    setDragId(null);
    if (!sourceId || sourceId === targetPid) return;
    // Only allow reorder within the same category.
    const source = items.find(x => x.product_id === sourceId);
    const target = items.find(x => x.product_id === targetPid);
    if (!source || !target || source.category_id !== target.category_id) return;
    const bucket = items.filter(x => x.category_id === targetCat);
    const srcIdx = bucket.findIndex(x => x.product_id === sourceId);
    const tgtIdx = bucket.findIndex(x => x.product_id === targetPid);
    if (srcIdx < 0 || tgtIdx < 0) return;
    const reordered = [...bucket];
    const [moved] = reordered.splice(srcIdx, 1);
    reordered.splice(tgtIdx, 0, moved);
    // Optimistic local update
    const next = [
      ...items.filter(x => x.category_id !== targetCat),
      ...reordered.map((p, i) => ({ ...p, sort_order: i })),
    ];
    setItems(next);
    try {
      await API.post('/admin/catalog/reorder', { order: reordered.map(p => p.product_id) });
      toast.success('Order saved');
    } catch (err) {
      toast.error('Save failed — reload');
      load();
    }
  };
  const visibleCats = catFilter ? cats.filter(c => c.category_id === catFilter) : cats;
  return (
    <div>
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <select
          className="pk-select"
          value={catFilter}
          onChange={e => setCatFilter(e.target.value)}
          data-testid="catalog-cat-filter"
        >
          <option value="">All categories</option>
          {cats.map(c => <option key={c.category_id} value={c.category_id}>{c.name}</option>)}
        </select>
        <div className="text-[10px] tracking-widest uppercase text-neutral-500 inline-flex items-center gap-1">
          <ArrowsDownUp size={11} weight="bold" /> Drag any product to reorder it inside its category
        </div>
        <div className="ml-auto">
          <button onClick={newProduct} className="pk-btn" data-testid="prd-new"><Plus size={14}/> Add Product</button>
        </div>
      </div>
      {visibleCats.map(cat => {
        const bucket = items.filter(p => p.category_id === cat.category_id);
        if (bucket.length === 0) return null;
        return (
          <div key={cat.category_id} className="mb-5" data-testid={`cat-group-${cat.category_id}`}>
            <div className="flex items-baseline gap-2 mb-2 border-b-2 border-black pb-1">
              <div className="font-display text-[14px]">{cat.name}</div>
              <div className="text-[9px] tracking-widest uppercase text-neutral-500 font-mono">{bucket.length} products</div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {bucket.map(p => {
                const totalOpts = (p.variant_groups || []).reduce((s, g) => s + (g.options || []).length, 0);
                const imgCount = (p.images || []).length || (p.image ? 1 : 0);
                const isDrag = dragId === p.product_id;
                return (
                  <div
                    key={p.product_id}
                    className={`pk-card p-3 transition-all ${isDrag ? 'opacity-40 scale-[0.98]' : ''}`}
                    draggable
                    onDragStart={onDragStart(p.product_id)}
                    onDragOver={onDragOver}
                    onDrop={onDrop(p.product_id, cat.category_id)}
                    onDragEnd={() => setDragId(null)}
                    data-testid={`prd-row-${p.product_id}`}
                  >
                    <div className="flex gap-3">
                      <div className="shrink-0 flex items-start pt-1 cursor-grab active:cursor-grabbing text-neutral-400" title="Drag to reorder" data-testid={`prd-drag-${p.product_id}`}>
                        <ArrowsDownUp size={14} weight="bold"/>
                      </div>
                      <div className="w-16 h-16 bg-neutral-100 border border-black overflow-hidden shrink-0 relative">
                        {(p.images?.[0] || p.image) && <img src={p.images?.[0] || p.image} alt="" className="w-full h-full object-cover"/>}
                        {imgCount > 1 && <span className="absolute bottom-0 right-0 bg-black text-white text-[8px] font-mono px-1">×{imgCount}</span>}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-bold text-[13px] truncate">{p.name}</div>
                        <div className="text-[10px] text-neutral-500 uppercase tracking-widest">{p.pricing_mode} · from ₹{p.base_price}</div>
                        <div className="text-[10px] font-mono text-neutral-400">
                          {(p.variant_groups?.length || 0) > 0
                            ? `${p.variant_groups.length} groups · ${totalOpts} options`
                            : `${(p.material_options || []).length} materials · ${(p.lamination_options || []).length} lam`}
                        </div>
                      </div>
                    </div>
                    <div className="mt-2 flex gap-2">
                      <button onClick={() => setEditing(JSON.parse(JSON.stringify(p)))} className="pk-chip text-[9px] flex-1" data-testid={`edit-prd-${p.product_id}`}><PencilSimple size={10}/> Edit</button>
                      <button onClick={() => del(p.product_id)} className="pk-chip text-[9px]" data-testid={`del-prd-${p.product_id}`}><Trash size={10}/></button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
      {editing && <ProductEditModal product={editing} cats={cats} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
    </div>
  );
};

const ProductEditModal = ({ product, cats, onClose, onSaved }) => {
  const [p, setP] = useState(() => ({
    ...product,
    images: product.images || (product.image ? [product.image] : []),
    variant_groups: product.variant_groups || [],
  }));
  const setF = (k) => (v) => setP({ ...p, [k]: v });

  const readFile = (file) => new Promise((res) => { const r = new FileReader(); r.onload = () => res(r.result); r.readAsDataURL(file); });

  // ---- Carousel images (up to 5) ----
  const addImage = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    if (f.size > 2 * 1024 * 1024) { toast.error('Image must be ≤2MB'); return; }
    const cur = p.images || [];
    if (cur.length >= 5) { toast.error('Max 5 images'); return; }
    const dataUrl = await readFile(f);
    const next = [...cur, dataUrl];
    setP({ ...p, images: next, image: p.image || dataUrl });
    e.target.value = '';
  };
  const removeImage = (idx) => {
    const next = (p.images || []).filter((_, i) => i !== idx);
    setP({ ...p, images: next, image: next.includes(p.image) ? p.image : (next[0] || '') });
  };
  const moveImage = (idx, dir) => {
    const arr = [...(p.images || [])];
    const j = idx + dir;
    if (j < 0 || j >= arr.length) return;
    [arr[idx], arr[j]] = [arr[j], arr[idx]];
    setP({ ...p, images: arr, image: arr[0] });
  };

  // ---- Variant groups ----
  const addGroup = () => setP({ ...p, variant_groups: [...(p.variant_groups || []), {
    id: `g${Date.now()}`, name: 'New group', required: true, options: []
  }]});
  const updateGroup = (gi, patch) => {
    const arr = [...p.variant_groups]; arr[gi] = { ...arr[gi], ...patch }; setP({ ...p, variant_groups: arr });
  };
  const removeGroup = (gi) => setP({ ...p, variant_groups: p.variant_groups.filter((_, i) => i !== gi) });
  const addOption = (gi) => {
    const arr = [...p.variant_groups];
    arr[gi] = { ...arr[gi], options: [...(arr[gi].options || []), {
      id: `o${Date.now()}`, label: '', mode: 'modifier', operator: '+', value: 0, image: ''
    }]};
    setP({ ...p, variant_groups: arr });
  };
  const updateOption = (gi, oi, patch) => {
    const arr = [...p.variant_groups];
    const opts = [...arr[gi].options]; opts[oi] = { ...opts[oi], ...patch };
    arr[gi] = { ...arr[gi], options: opts };
    setP({ ...p, variant_groups: arr });
  };
  const removeOption = (gi, oi) => {
    const arr = [...p.variant_groups];
    arr[gi] = { ...arr[gi], options: arr[gi].options.filter((_, i) => i !== oi) };
    setP({ ...p, variant_groups: arr });
  };
  const setOptionImage = async (gi, oi, e) => {
    const f = e.target.files?.[0]; if (!f) return;
    if (f.size > 1.5 * 1024 * 1024) { toast.error('Option image ≤1.5MB'); return; }
    const dataUrl = await readFile(f);
    updateOption(gi, oi, { image: dataUrl });
    e.target.value = '';
  };

  const save = async () => {
    try {
      const payload = {
        name: p.name,
        category_id: p.category_id,
        description: p.description || '',
        image: p.image || (p.images?.[0] || null),
        images: (p.images || []).slice(0, 5),
        pricing_mode: p.pricing_mode,
        base_price: parseFloat(p.base_price) || 0,
        min_qty: parseInt(p.min_qty, 10) || 1,
        min_sqft: (p.min_sqft === '' || p.min_sqft === null || p.min_sqft === undefined) ? null : (parseFloat(p.min_sqft) || null),
        variant_groups: (p.variant_groups || []).map(g => ({
          id: g.id, name: g.name || '', required: !!g.required,
          layout: g.layout || 'auto',
          options: (g.options || []).map(o => ({
            id: o.id, label: o.label || '',
            sublabel: o.sublabel || null,
            mode: o.mode === 'replace' ? 'replace' : 'modifier',
            operator: o.operator || '+',
            value: parseFloat(o.value) || 0,
            image: o.image || null,
          })),
        })),
        // Legacy fields (preserved if the product predates variant_groups)
        material_options: (p.material_options || []).map(m => ({ ...m, surcharge: parseFloat(m.surcharge) || 0 })),
        lamination_options: (p.lamination_options || []).map(l => ({ ...l, surcharge: parseFloat(l.surcharge) || 0 })),
        size_options: p.size_options || [],
        required_machine_types: p.required_machine_types || [],
        has_lamination: (p.lamination_options || []).length > 0 || !!p.has_lamination,
        lamination_surcharge: parseFloat(p.lamination_surcharge) || 0,
      };
      if (p.product_id) await API.put(`/admin/catalog/${p.product_id}`, payload);
      else await API.post('/admin/catalog', payload);
      toast.success('Saved');
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white pk-border-thick brutal-shadow-lg max-w-3xl w-full max-h-[90vh] overflow-y-auto p-6" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <div className="font-display text-[22px]">{p.product_id ? 'Edit product' : 'New product'}</div>
          <button onClick={onClose} className="pk-chip"><XIcon size={12}/></button>
        </div>

        <div className="mt-4 grid grid-cols-2 gap-3">
          <div className="col-span-2"><label className="pk-label">Name</label><input className="pk-input" value={p.name} onChange={e => setF('name')(e.target.value)} data-testid="pe-name"/></div>
          <div><label className="pk-label">Category</label>
            <select className="pk-select" value={p.category_id} onChange={e => setF('category_id')(e.target.value)}>
              {cats.map(c => <option key={c.category_id} value={c.category_id}>{c.name}</option>)}
            </select>
          </div>
          <div><label className="pk-label">Pricing mode</label>
            <select className="pk-select" value={p.pricing_mode} onChange={e => setF('pricing_mode')(e.target.value)}>
              <option value="per_unit">per unit</option><option value="per_sqft">per sqft</option><option value="fixed">fixed</option><option value="callback">callback</option>
            </select>
          </div>
          <div><label className="pk-label">Base price</label><input type="number" className="pk-input font-mono" value={p.base_price} onChange={e => setF('base_price')(e.target.value)}/></div>
          <div><label className="pk-label">Min qty</label><input type="number" className="pk-input font-mono" value={p.min_qty} onChange={e => setF('min_qty')(e.target.value)}/></div>
          {p.pricing_mode === 'per_sqft' && (
            <div><label className="pk-label">Min sqft</label>
              <input type="number" step="any" className="pk-input font-mono" value={p.min_sqft ?? ''} placeholder="e.g. 30" onChange={e => setF('min_sqft')(e.target.value === '' ? null : Number(e.target.value))} data-testid="pe-min-sqft"/>
              <p className="text-[10px] text-neutral-500 mt-1">Charged sqft = max(width × height × qty, min sqft).</p>
            </div>
          )}
          <div className="col-span-2"><label className="pk-label">Description</label><textarea rows={2} className="pk-textarea" value={p.description || ''} onChange={e => setF('description')(e.target.value)}/></div>
        </div>

        {/* Carousel images */}
        <div className="mt-4">
          <div className="flex items-center justify-between">
            <div className="pk-label mb-0">Carousel images ({(p.images || []).length}/5)</div>
            <label className={`pk-chip ${(p.images || []).length >= 5 ? 'opacity-50 pointer-events-none' : 'cursor-pointer'}`} data-testid="pe-add-image">
              <Plus size={10}/> Add image
              <input type="file" accept="image/*" onChange={addImage} className="hidden" disabled={(p.images || []).length >= 5} />
            </label>
          </div>
          <div className="mt-2 grid grid-cols-5 gap-2">
            {(p.images || []).map((src, i) => (
              <div key={i} className="pk-card p-1 relative" data-testid={`pe-image-${i}`}>
                <img src={src} alt="" className="w-full aspect-square object-cover border border-black" />
                <div className="flex items-center justify-between mt-1">
                  <button onClick={() => moveImage(i, -1)} className="pk-chip text-[9px] px-1" title="Move left" disabled={i === 0}>←</button>
                  <span className="text-[9px] font-mono">#{i + 1}</span>
                  <button onClick={() => moveImage(i, 1)} className="pk-chip text-[9px] px-1" title="Move right" disabled={i === (p.images || []).length - 1}>→</button>
                </div>
                <button onClick={() => removeImage(i)} className="absolute -top-2 -right-2 w-5 h-5 bg-white border-2 border-black flex items-center justify-center" title="Remove"><Trash size={9}/></button>
              </div>
            ))}
            {(p.images || []).length === 0 && <div className="col-span-5 text-[11px] text-neutral-400 py-3 text-center pk-border border-dashed">No images yet — first image becomes the primary thumbnail.</div>}
          </div>
        </div>

        {/* Variant groups */}
        <div className="mt-5">
          <div className="flex items-center justify-between">
            <div className="pk-label mb-0">Variant groups</div>
            <button onClick={addGroup} className="pk-chip" data-testid="pe-add-group"><Plus size={10}/> Add group</button>
          </div>
          <div className="text-[10px] text-neutral-500 mt-1">Each group is a heading (e.g. Material, Size, Lamination). Each option modifies the base price using an operator, or replaces it entirely.</div>

          <div className="mt-3 space-y-4">
            {(p.variant_groups || []).map((g, gi) => (
              <div key={g.id} className="pk-border p-3 bg-neutral-50" data-testid={`pe-group-${gi}`}>
                <div className="grid grid-cols-12 gap-2 items-end">
                  <div className="col-span-5">
                    <label className="pk-label">Group name</label>
                    <input className="pk-input" placeholder="e.g. Material" value={g.name} onChange={e => updateGroup(gi, { name: e.target.value })} data-testid={`pe-group-name-${gi}`}/>
                  </div>
                  <div className="col-span-3">
                    <label className="pk-label">Layout</label>
                    <select
                      className="pk-select"
                      value={g.layout || 'auto'}
                      onChange={e => updateGroup(gi, { layout: e.target.value })}
                      data-testid={`pe-group-layout-${gi}`}
                    >
                      <option value="auto">Auto</option>
                      <option value="grid">2-col grid</option>
                      <option value="list">Full-width list</option>
                    </select>
                  </div>
                  <div className="col-span-2 flex items-center gap-2 pb-2">
                    <label className="flex items-center gap-1 text-[11px]">
                      <input type="checkbox" checked={!!g.required} onChange={e => updateGroup(gi, { required: e.target.checked })}/>
                      Required
                    </label>
                  </div>
                  <div className="col-span-2 flex justify-end">
                    <button onClick={() => removeGroup(gi)} className="pk-chip" data-testid={`pe-del-group-${gi}`}><Trash size={10}/></button>
                  </div>
                </div>

                <div className="mt-3 space-y-2">
                  {(g.options || []).map((o, oi) => (
                    <div key={o.id} className="pk-card p-2 space-y-2" data-testid={`pe-opt-${gi}-${oi}`}>
                      <div className="grid grid-cols-12 gap-2 items-center">
                        <input className="pk-input col-span-3" placeholder="Label (e.g. A4)" value={o.label} onChange={e => updateOption(gi, oi, { label: e.target.value })} data-testid={`pe-opt-label-${gi}-${oi}`}/>
                        <input className="pk-input col-span-3" placeholder="Sublabel (e.g. 210 × 297 mm)" value={o.sublabel || ''} onChange={e => updateOption(gi, oi, { sublabel: e.target.value })} data-testid={`pe-opt-sub-${gi}-${oi}`}/>
                        <select className="pk-select col-span-2" value={o.mode} onChange={e => updateOption(gi, oi, { mode: e.target.value })} data-testid={`pe-opt-mode-${gi}-${oi}`}>
                          <option value="modifier">modify</option>
                          <option value="replace">replace</option>
                        </select>
                        {o.mode === 'modifier' && (
                          <select className="pk-select col-span-1 font-mono text-center" value={o.operator} onChange={e => updateOption(gi, oi, { operator: e.target.value })} data-testid={`pe-opt-op-${gi}-${oi}`}>
                            <option value="+">+</option><option value="-">−</option><option value="*">×</option><option value="/">÷</option>
                          </select>
                        )}
                        <input type="number" step="any" className={`pk-input font-mono ${o.mode === 'modifier' ? 'col-span-2' : 'col-span-3'}`} placeholder="Value" value={o.value} onChange={e => updateOption(gi, oi, { value: e.target.value })} data-testid={`pe-opt-value-${gi}-${oi}`}/>
                        <button onClick={() => removeOption(gi, oi)} className="pk-chip col-span-1 justify-center" title="Remove option"><Trash size={10}/></button>
                      </div>
                      <div className="flex items-center gap-2">
                        {o.image && <img src={o.image} alt="" className="w-8 h-8 object-cover border border-black" />}
                        <label className="pk-chip text-[9px] cursor-pointer">
                          {o.image ? 'Change photo' : 'Add photo'}
                          <input type="file" accept="image/*" onChange={e => setOptionImage(gi, oi, e)} className="hidden" data-testid={`pe-opt-img-${gi}-${oi}`}/>
                        </label>
                      </div>
                    </div>
                  ))}
                  <button onClick={() => addOption(gi)} className="pk-chip w-full justify-center" data-testid={`pe-add-opt-${gi}`}><Plus size={10}/> Add option</button>
                </div>
              </div>
            ))}
            {(p.variant_groups || []).length === 0 && <div className="text-[11px] text-neutral-400 py-2 text-center pk-border border-dashed">No variant groups yet — customers will see only the base price.</div>}
          </div>
        </div>

        <div className="mt-6 flex gap-2">
          <button onClick={onClose} className="pk-btn pk-btn-outline flex-1">Cancel</button>
          <button onClick={save} className="pk-btn pk-btn-magenta flex-1" data-testid="pe-save">Save product</button>
        </div>
      </div>
    </div>
  );
};

// ------------- Branding Tab -------------
const BrandingTab = ({ onSaved }) => {
  const [b, setB] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => { API.get('/branding').then(r => setB(r.data)); }, []);
  const upd = (k) => (v) => setB({ ...b, [k]: v });
  const readFile = (file) => new Promise((resolve) => { const r = new FileReader(); r.onload = () => resolve(r.result); r.readAsDataURL(file); });
  const onLogo = async (e) => { const f = e.target.files?.[0]; if (!f) return; if (f.size > 2*1024*1024) { toast.error('Max 2MB'); return; } upd('logo_data_url')(await readFile(f)); };
  const onHero = async (e) => { const f = e.target.files?.[0]; if (!f) return; if (f.size > 5*1024*1024) { toast.error('Max 5MB'); return; } upd('hero_banner_data_url')(await readFile(f)); };
  const addBanner = async (e) => { const f = e.target.files?.[0]; if (!f) return; if ((b.banners || []).length >= 3) { toast.error('Max 3 banners'); return; } const dataUrl = await readFile(f); setB({ ...b, banners: [...(b.banners || []), { image_data_url: dataUrl, link_type: 'category', link_id: '', alt: 'offer' }] }); };
  const addClientLogo = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    const existing = b.client_logos || [];
    if (existing.length + files.length > 20) { toast.error('Max 20 client logos'); return; }
    const newOnes = [];
    for (const f of files) {
      if (f.size > 1 * 1024 * 1024) { toast.error(`${f.name} > 1MB — skipped`); continue; }
      newOnes.push({ name: f.name.replace(/\.[^.]+$/, '').replace(/[-_]+/g, ' '), logo_data_url: await readFile(f) });
    }
    if (newOnes.length) setB({ ...b, client_logos: [...existing, ...newOnes] });
    e.target.value = ''; // allow re-selecting same file
  };
  const save = async () => {
    setSaving(true);
    try { await API.put('/branding', b); toast.success('Saved'); onSaved && onSaved(); }
    catch { toast.error('Failed'); } finally { setSaving(false); }
  };
  if (!b) return <div className="p-4">Loading…</div>;
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <div className="space-y-3">
        <div><label className="pk-label">Brand name</label><input className="pk-input" value={b.logo_text || ''} onChange={e => upd('logo_text')(e.target.value)} data-testid="br-name"/></div>
        <div><label className="pk-label">Tagline</label><input className="pk-input" value={b.logo_tagline || ''} onChange={e => upd('logo_tagline')(e.target.value)}/></div>
        <div><label className="pk-label">Primary color</label><input type="color" className="pk-input h-12" value={b.primary_color || '#e30066'} onChange={e => upd('primary_color')(e.target.value.slice(0, 7))} data-testid="br-color"/></div>
        <div><label className="pk-label">Logo (≤2MB)</label><input type="file" accept="image/*" onChange={onLogo}/></div>
        <div><label className="pk-label">Hero banner (square, ≤5MB)</label><input type="file" accept="image/*" onChange={onHero}/></div>
        <div className="grid grid-cols-3 gap-2">
          <div><label className="pk-label">Banner link type</label>
            <select className="pk-select" value={b.hero_banner_link_type || 'none'} onChange={e => upd('hero_banner_link_type')(e.target.value)} data-testid="br-hero-link-type">
              <option value="none">none (not clickable)</option>
              <option value="product">product</option>
              <option value="category">category</option>
              <option value="url">custom URL</option>
            </select>
          </div>
          <div className="col-span-2"><label className="pk-label">Banner link target</label>
            <input className="pk-input" placeholder="prd_flyers / cat_paper / https://..." value={b.hero_banner_link_id || ''} onChange={e => upd('hero_banner_link_id')(e.target.value)} data-testid="br-hero-link-id" disabled={(b.hero_banner_link_type || 'none') === 'none'}/>
          </div>
        </div>
        <div><label className="pk-label">Contact phone</label><input className="pk-input font-mono" value={b.contact_phone || ''} onChange={e => upd('contact_phone')(e.target.value)}/></div>
        <div className="grid grid-cols-2 gap-2">
          <div><label className="pk-label">Banner columns</label>
            <select className="pk-select" value={b.banners_columns || 2} onChange={e => upd('banners_columns')(parseInt(e.target.value, 10))}>
              <option value={1}>1</option><option value={2}>2</option><option value={3}>3</option>
            </select>
          </div>
          <div><label className="pk-label">Banner height</label>
            <select className="pk-select" value={b.banners_height || 'medium'} onChange={e => upd('banners_height')(e.target.value)}>
              <option value="small">small</option><option value="medium">medium</option><option value="large">large</option>
            </select>
          </div>
        </div>
        <div>
          <label className="pk-label">Offer banners ({(b.banners || []).length}/3)</label>
          <input type="file" accept="image/*" onChange={addBanner} disabled={(b.banners || []).length >= 3}/>
          <div className="mt-2 space-y-1">
            {(b.banners || []).map((bn, i) => (
              <div key={i} className="pk-card p-2 flex items-center gap-2">
                <img src={bn.image_data_url} alt="" className="w-10 h-10 object-cover border border-black"/>
                <input placeholder="link id (category or product)" className="pk-input" value={bn.link_id} onChange={e => { const arr = [...b.banners]; arr[i] = { ...arr[i], link_id: e.target.value }; upd('banners')(arr); }}/>
                <select value={bn.link_type} onChange={e => { const arr = [...b.banners]; arr[i] = { ...arr[i], link_type: e.target.value }; upd('banners')(arr); }} className="pk-select w-28">
                  <option value="category">category</option><option value="product">product</option>
                </select>
                <button onClick={() => upd('banners')(b.banners.filter((_, j) => j !== i))} className="pk-chip"><Trash size={10}/></button>
              </div>
            ))}
          </div>
        </div>
        <div>
          <label className="pk-label">Enterprise client logos ({(b.client_logos || []).length}/20) — looped marquee</label>
          <input type="file" accept="image/png,image/jpeg,image/webp,image/svg+xml" multiple onChange={addClientLogo} disabled={(b.client_logos || []).length >= 20} data-testid="br-client-logos-upload"/>
          <p className="text-[10px] text-neutral-500 mt-1">PNG or SVG on transparent bg looks best. Max 1MB each, up to 20 logos. Runs as a smooth marquee on the Enterprise page.</p>
          {(b.client_logos || []).length > 0 && (
            <div className="mt-2 grid grid-cols-2 gap-1.5" data-testid="br-client-logos-list">
              {(b.client_logos || []).map((cl, i) => (
                <div key={i} className="pk-card p-2 flex items-center gap-2">
                  <img src={cl.logo_data_url} alt={cl.name} className="w-10 h-10 object-contain border border-black bg-white shrink-0"/>
                  <input placeholder="Client name" className="pk-input" value={cl.name || ''} onChange={e => { const arr = [...b.client_logos]; arr[i] = { ...arr[i], name: e.target.value }; upd('client_logos')(arr); }}/>
                  <button onClick={() => upd('client_logos')(b.client_logos.filter((_, j) => j !== i))} className="pk-chip" data-testid={`br-client-logo-remove-${i}`}><Trash size={10}/></button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
      <div className="space-y-3">
        <div><label className="pk-label">Hero overline</label><input className="pk-input" value={b.hero_overline || ''} onChange={e => upd('hero_overline')(e.target.value)}/></div>
        <div><label className="pk-label">Hero line 1</label><input className="pk-input" value={b.hero_line_1 || ''} onChange={e => upd('hero_line_1')(e.target.value)}/></div>
        <div><label className="pk-label">Hero line 2 (highlighted)</label><input className="pk-input" value={b.hero_line_2 || ''} onChange={e => upd('hero_line_2')(e.target.value)}/></div>
        <div><label className="pk-label">Hero line 3 (accent)</label><input className="pk-input" value={b.hero_line_3 || ''} onChange={e => upd('hero_line_3')(e.target.value)}/></div>
        <div><label className="pk-label">Hero subtext</label><textarea rows={3} className="pk-textarea" value={b.hero_subtext || ''} onChange={e => upd('hero_subtext')(e.target.value)}/></div>
        <div><label className="pk-label">Footer note</label><textarea rows={2} className="pk-textarea" value={b.footer_note || ''} onChange={e => upd('footer_note')(e.target.value)}/></div>
        <div><label className="pk-label">Powered by</label><input className="pk-input" value={b.powered_by || ''} onChange={e => upd('powered_by')(e.target.value)} data-testid="br-powered-by"/></div>
        <button onClick={save} disabled={saving} className="pk-btn w-full" data-testid="br-save">{saving ? 'Saving…' : 'Save branding'}</button>
      </div>
    </div>
  );
};

// ------------- Cashouts Tab -------------
const CashoutsTab = () => {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState('pending');
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState({ settled_amount: '', transaction_ref: '', settle_note: '' });
  const load = async () => { try { const r = await API.get('/admin/cashouts', { params: filter === 'all' ? {} : { status: filter } }); setItems(r.data); } catch (e) { console.error('[admin] load cashouts failed', e); } };
  useEffect(() => { load(); }, [filter]);
  const runMonthly = async () => { if (!window.confirm('Create monthly cashout requests for all eligible partners?')) return; try { const r = await API.post('/admin/cashouts/process-monthly'); toast.success(r.data.already ? 'Already run today' : `Created ${r.data.created}`); load(); } catch { toast.error('Failed'); } };
  const openSettle = (c) => { setSelected(c); setForm({ settled_amount: c.amount, transaction_ref: '', settle_note: '' }); };
  const settle = async (e) => { e.preventDefault(); try { await API.post(`/admin/cashouts/${selected.cashout_id}/settle`, { settled_amount: parseFloat(form.settled_amount), transaction_ref: form.transaction_ref, settle_note: form.settle_note }); toast.success('Settled'); setSelected(null); load(); } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); } };
  const reject = async (c) => { const reason = window.prompt('Reason?'); if (reason === null) return; try { await API.post(`/admin/cashouts/${c.cashout_id}/reject`, { reason }); toast.success('Rejected'); load(); } catch { toast.error('Failed'); } };
  return (
    <div>
      <div className="flex items-center gap-2 mb-3">
        {['pending','settled','rejected','all'].map(s => <button key={s} onClick={() => setFilter(s)} className={`pk-chip ${filter === s ? 'active' : ''}`} data-testid={`cashout-filter-${s}`}>{s}</button>)}
        <button onClick={runMonthly} className="pk-btn ml-auto" data-testid="run-monthly"><Money size={14}/> Process Monthly</button>
      </div>
      <table className="w-full text-[12px]">
        <thead className="bg-neutral-100"><tr className="text-left"><th className="p-2 text-[9px] tracking-widest uppercase">Partner</th><th className="p-2 text-[9px] tracking-widest uppercase">UPI</th><th className="p-2 text-[9px] tracking-widest uppercase">Amount</th><th className="p-2 text-[9px] tracking-widest uppercase">Status</th><th className="p-2 text-[9px] tracking-widest uppercase">Requested</th><th className="p-2 text-[9px] tracking-widest uppercase text-right">Actions</th></tr></thead>
        <tbody>
          {items.map(c => (
            <tr key={c.cashout_id} className="border-t border-neutral-200 hover:bg-yellow-50">
              <td className="p-2">{c.partner_name}<div className="text-[10px] text-neutral-500">{c.partner_email}</div></td>
              <td className="p-2 font-mono">{c.upi_id || '—'}</td>
              <td className="p-2 font-mono font-bold">₹{(c.amount || 0).toLocaleString('en-IN')}{c.settled_amount ? <div className="text-[10px] text-neutral-500">settled ₹{c.settled_amount}</div> : null}</td>
              <td className="p-2"><span className={`status-${c.status === 'settled' ? 'delivered' : c.status === 'rejected' ? 'out_for_delivery' : 'accepted'} border border-black text-[9px] tracking-widest uppercase px-1.5 py-0.5 font-bold`}>{c.status}</span>
                {c.transaction_ref && <div className="text-[10px] font-mono mt-1"><b>UTR:</b> {c.transaction_ref}</div>}
                {c.settle_note && <div className="text-[10px] text-neutral-500 italic mt-0.5">"{c.settle_note}"</div>}
              </td>
              <td className="p-2 font-mono text-[10px]">{c.requested_at?.slice(0,16).replace('T',' ')}{c.settled_at && <div className="text-neutral-400">→ {c.settled_at.slice(0,16).replace('T',' ')}</div>}</td>
              <td className="p-2 text-right">
                {c.status === 'pending' && <>
                  <button onClick={() => openSettle(c)} className="pk-chip text-[9px]" data-testid={`settle-${c.cashout_id}`}><Check size={10}/> Settle</button>
                  <button onClick={() => reject(c)} className="pk-chip text-[9px] ml-1" data-testid={`reject-${c.cashout_id}`}><XIcon size={10}/> Reject</button>
                </>}
              </td>
            </tr>
          ))}
          {items.length === 0 && <tr><td colSpan={6} className="p-6 text-center text-neutral-500">No cashouts.</td></tr>}
        </tbody>
      </table>

      {selected && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={() => setSelected(null)}>
          <form onSubmit={settle} className="bg-white pk-border-thick brutal-shadow-lg max-w-lg w-full p-6 slide-up" onClick={e => e.stopPropagation()}>
            <div className="font-display text-[20px]">Settle cashout</div>
            <div className="text-[11px] text-neutral-500 mt-1">{selected.partner_name} · Requested ₹{selected.amount}</div>
            <div className="mt-4 space-y-3">
              <div><label className="pk-label">Settled amount</label><input required type="number" className="pk-input font-mono" value={form.settled_amount} onChange={e => setForm({ ...form, settled_amount: e.target.value })} data-testid="settle-amount"/></div>
              <div><label className="pk-label">Transaction ref (UPI / NEFT ID)</label><input required className="pk-input font-mono" value={form.transaction_ref} onChange={e => setForm({ ...form, transaction_ref: e.target.value })} data-testid="settle-txn"/></div>
              <div><label className="pk-label">Note (optional)</label><input className="pk-input" value={form.settle_note} onChange={e => setForm({ ...form, settle_note: e.target.value })}/></div>
            </div>
            <div className="mt-4 flex gap-2">
              <button type="button" onClick={() => setSelected(null)} className="pk-btn pk-btn-outline flex-1">Cancel</button>
              <button type="submit" className="pk-btn pk-btn-magenta flex-1" data-testid="settle-confirm">Confirm settlement</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

// ------------- Users Tab -------------
const UsersTab = () => {
  const [users, setUsers] = useState([]);
  const [q, setQ] = useState('');
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);

  const load = async () => {
    try { const r = await API.get('/admin/users'); setUsers(r.data); }
    catch { toast.error('Failed to load users'); }
  };
  useEffect(() => { load(); }, []);

  const openUser = async (u) => {
    setSelected(u); setDetail(null);
    try { const r = await API.get(`/admin/users/${u.user_id}`); setDetail(r.data); }
    catch { toast.error('Could not load user details'); }
  };

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (!term) return users;
    return users.filter(u =>
      (u.name || '').toLowerCase().includes(term) ||
      (u.email || '').toLowerCase().includes(term) ||
      (u.phone || '').toLowerCase().includes(term) ||
      (u.delivery_pincodes || []).some(p => (p || '').includes(term))
    );
  }, [users, q]);

  const exportCsv = () => {
    // Server streams a proper CSV with disposition — open in a new tab which triggers download
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/admin/users/export.csv`;
    const a = document.createElement('a');
    a.href = url;
    a.download = 'printingkit-customers.csv';
    document.body.appendChild(a); a.click(); a.remove();
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <input value={q} onChange={e => setQ(e.target.value)} placeholder="Search by name, email, phone or pincode…" className="pk-input flex-1" data-testid="users-search" />
        <button onClick={exportCsv} className="pk-chip" data-testid="users-export-csv"><FileArrowDown size={12}/> Export CSV</button>
      </div>
      <div className="pk-card overflow-x-auto">
        <table className="w-full text-[12px]">
          <thead className="bg-neutral-100"><tr className="text-left">
            <th className="p-2 text-[9px] tracking-widest uppercase">Name</th>
            <th className="p-2 text-[9px] tracking-widest uppercase">Email</th>
            <th className="p-2 text-[9px] tracking-widest uppercase">Phone</th>
            <th className="p-2 text-[9px] tracking-widest uppercase">Pincodes</th>
            <th className="p-2 text-[9px] tracking-widest uppercase text-right">Orders</th>
            <th className="p-2 text-[9px] tracking-widest uppercase text-right">Total Value</th>
            <th className="p-2 text-[9px] tracking-widest uppercase text-right">Actions</th>
          </tr></thead>
          <tbody>
            {filtered.map(u => (
              <tr key={u.user_id} className="border-t border-neutral-200 hover:bg-yellow-50" data-testid={`user-row-${u.user_id}`}>
                <td className="p-2 font-bold cursor-pointer" onClick={() => openUser(u)}>{u.name}</td>
                <td className="p-2 font-mono text-[11px]">{u.email}</td>
                <td className="p-2 font-mono text-[11px]">{u.phone || '—'}</td>
                <td className="p-2 font-mono text-[10px]">{(u.delivery_pincodes || []).join(', ') || '—'}</td>
                <td className="p-2 text-right font-mono">
                  <span className="font-bold">{u.orders_count}</span>
                  <div className="text-[9px] tracking-widest uppercase text-neutral-500">{u.delivered_orders_count} delivered</div>
                </td>
                <td className="p-2 text-right font-mono font-bold">₹{(u.total_value || 0).toLocaleString('en-IN')}</td>
                <td className="p-2 text-right"><button onClick={() => openUser(u)} className="pk-chip text-[9px]" data-testid={`view-user-${u.user_id}`}><Eye size={10}/> View</button></td>
              </tr>
            ))}
            {filtered.length === 0 && <tr><td colSpan={7} className="p-6 text-center text-neutral-500">No customers found.</td></tr>}
          </tbody>
        </table>
      </div>

      {/* User drill-down modal */}
      {selected && (
        <div className="fixed inset-0 bg-black/50 z-[9990] flex items-center justify-center p-4" onClick={() => setSelected(null)}>
          <div className="bg-white pk-border-thick brutal-shadow-lg max-w-3xl w-full max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="p-4 border-b-2 border-black flex items-start justify-between">
              <div>
                <div className="text-[10px] tracking-[0.22em] uppercase text-neutral-500">Customer</div>
                <div className="font-display text-[22px] leading-tight">{selected.name}</div>
                <div className="text-[11px] font-mono mt-1">{selected.email} · {selected.phone || 'no phone'}</div>
              </div>
              <button onClick={() => setSelected(null)} className="pk-chip"><XIcon size={12}/> Close</button>
            </div>
            <div className="p-4 grid grid-cols-4 gap-2">
              <div className="pk-card p-2"><div className="text-[9px] uppercase tracking-widest text-neutral-500">Orders</div><div className="font-stat text-[24px]">{selected.orders_count}</div></div>
              <div className="pk-card p-2"><div className="text-[9px] uppercase tracking-widest text-neutral-500">Paid</div><div className="font-stat text-[24px]">{selected.paid_orders_count}</div></div>
              <div className="pk-card p-2"><div className="text-[9px] uppercase tracking-widest text-neutral-500">Delivered</div><div className="font-stat text-[24px]">{selected.delivered_orders_count}</div></div>
              <div className="pk-card p-2" style={{ background: 'var(--pk-yellow)' }}><div className="text-[9px] uppercase tracking-widest">Total Value</div><div className="font-stat text-[20px]">₹{(selected.total_value || 0).toLocaleString('en-IN')}</div></div>
            </div>
            <div className="p-4">
              <div className="text-[10px] tracking-[0.22em] uppercase font-bold mb-2">Order history</div>
              {!detail && <div className="text-[12px] text-neutral-500">Loading…</div>}
              {detail?.orders?.length === 0 && <div className="text-[12px] text-neutral-500">No orders yet.</div>}
              {detail?.orders?.length > 0 && (
                <table className="w-full text-[11px]">
                  <thead className="bg-neutral-100"><tr className="text-left">
                    <th className="p-2 text-[9px] uppercase tracking-widest">Order</th>
                    <th className="p-2 text-[9px] uppercase tracking-widest">Items</th>
                    <th className="p-2 text-[9px] uppercase tracking-widest">Partner</th>
                    <th className="p-2 text-[9px] uppercase tracking-widest">Status</th>
                    <th className="p-2 text-[9px] uppercase tracking-widest">Placed</th>
                    <th className="p-2 text-[9px] uppercase tracking-widest">Delivered</th>
                    <th className="p-2 text-[9px] uppercase tracking-widest text-right">Total</th>
                  </tr></thead>
                  <tbody>
                    {detail.orders.map(o => (
                      <tr key={o.order_id} className="border-t border-neutral-200" data-testid={`user-order-row-${o.order_id}`}>
                        <td className="p-2 font-mono text-[10px]">#{o.order_id.slice(-8)}</td>
                        <td className="p-2">{o.items.map(i => `${i.quantity}× ${i.product_name}`).join(', ')}</td>
                        <td className="p-2">{o.partner_name || '—'}</td>
                        <td className="p-2"><span className={`status-${o.status} border border-black text-[9px] tracking-widest uppercase px-1.5 py-0.5 font-bold`}>{o.status}</span></td>
                        <td className="p-2 font-mono text-[10px]">{o.created_at?.slice(0, 10)}</td>
                        <td className="p-2 font-mono text-[10px]">{o.delivered_at?.slice(0, 10) || '—'}</td>
                        <td className="p-2 text-right font-mono font-bold">₹{(o.total_amount || 0).toLocaleString('en-IN')}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ------------- Support Tab -------------
const ENTERPRISE_STAGES = [
  { id: 'new', label: 'New', color: '#e30066', bg: '#fff', textOnBg: '#e30066' },
  { id: 'follow_up', label: 'Follow-up', color: '#f59e0b', bg: '#fef3c7', textOnBg: '#92400e' },
  { id: 'hot', label: 'Hot lead', color: '#ef4444', bg: '#fee2e2', textOnBg: '#991b1b' },
  { id: 'potential', label: 'Potential', color: '#00a3e0', bg: '#e0f2fe', textOnBg: '#075985' },
  { id: 'cold', label: 'Cold lead', color: '#64748b', bg: '#f1f5f9', textOnBg: '#334155' },
  { id: 'converted', label: 'Converted ✓', color: '#10b981', bg: '#d1fae5', textOnBg: '#065f46' },
  { id: 'lost', label: 'Lost', color: '#525252', bg: '#e5e5e5', textOnBg: '#404040' },
];
const stageMeta = (id) => ENTERPRISE_STAGES.find(s => s.id === id) || ENTERPRISE_STAGES[0];

const StageChip = ({ inquiry, onChange }) => {
  const [open, setOpen] = useState(false);
  const [rect, setRect] = useState(null);
  const btnRef = React.useRef(null);
  const meta = stageMeta(inquiry.stage || 'new');
  const toggle = () => {
    if (open) { setOpen(false); return; }
    const r = btnRef.current?.getBoundingClientRect();
    const dropdownHeight = Math.min(7 * 36 + 4, 280); // ~36px per row, capped by max-h
    // Flip upward if placing below would exceed viewport
    const spaceBelow = window.innerHeight - r.bottom;
    const flipUp = spaceBelow < dropdownHeight + 16;
    setRect({
      top: flipUp ? r.top - dropdownHeight - 4 : r.bottom + 4,
      left: r.left,
    });
    setOpen(true);
  };
  return (
    <div className="relative inline-block">
      <button
        ref={btnRef}
        onClick={toggle}
        className="inline-flex items-center gap-1 border-2 border-black px-2 py-0.5 text-[10px] tracking-[0.14em] uppercase font-bold"
        style={{ background: meta.bg, color: meta.textOnBg }}
        data-testid={`enq-stage-${inquiry.inquiry_id}`}
      >
        <span className="w-1.5 h-1.5" style={{ background: meta.color }} />
        {meta.label}
      </button>
      {open && rect && ReactDOM.createPortal(
        <>
          <div className="fixed inset-0 z-[9998]" onClick={() => setOpen(false)} />
          <div
            className="fixed z-[9999] min-w-[180px] bg-white border-2 border-black brutal-shadow-sm max-h-[280px] overflow-y-auto"
            style={{ top: rect.top, left: rect.left }}
          >
            {ENTERPRISE_STAGES.map(s => (
              <button
                key={s.id}
                onClick={() => { setOpen(false); onChange(s.id); }}
                className="w-full text-left px-3 py-2 text-[11px] uppercase tracking-widest font-bold flex items-center gap-2 hover:bg-neutral-100 border-b border-neutral-200 last:border-b-0"
                data-testid={`enq-stage-opt-${inquiry.inquiry_id}-${s.id}`}
              >
                <span className="w-2 h-2" style={{ background: s.color }} />
                {s.label}
              </button>
            ))}
          </div>
        </>,
        document.body
      )}
    </div>
  );
};

const SupportTab = () => {
  const [tickets, setTickets] = useState([]);
  const [inquiries, setInquiries] = useState([]);
  const [summary, setSummary] = useState(null);
  const [sub, setSub] = useState('tickets'); // tickets | enterprise
  const [resolveSel, setResolveSel] = useState(null);
  const [rf, setRf] = useState({ resolution_type: 'resolved_call', resolution_note: '' });
  const [stageFilter, setStageFilter] = useState('all');
  const load = async () => {
    const r = await API.get('/support/tickets'); setTickets(r.data);
    try { const s = await API.get('/support/summary'); setSummary(s.data); } catch { /* ignore */ }
    try { const e = await API.get('/admin/enterprise'); setInquiries(e.data); } catch { /* ignore */ }
  };
  useEffect(() => { load(); }, []);
  const resolve = async (e) => {
    e.preventDefault();
    try { await API.post(`/support/tickets/${resolveSel.ticket_id}/resolve`, rf); toast.success('Resolved'); setResolveSel(null); load(); }
    catch (err) { toast.error('Failed'); }
  };
  const quickClose = async (tid) => { try { await API.post(`/support/tickets/${tid}/close`); toast.success('Closed'); load(); } catch { toast.error('Failed'); } };

  const changeStage = async (iid, stage) => {
    try {
      await API.patch(`/admin/enterprise/${iid}`, { stage });
      toast.success(`Marked as ${stageMeta(stage).label}`);
      load();
    } catch { toast.error('Failed to update stage'); }
  };

  const filteredInquiries = useMemo(() =>
    stageFilter === 'all' ? inquiries : inquiries.filter(i => (i.stage || 'new') === stageFilter),
    [inquiries, stageFilter]
  );

  const stageCount = (id) => id === 'all' ? inquiries.length : inquiries.filter(i => (i.stage || 'new') === id).length;

  const newInquiryCount = inquiries.filter(i => (i.stage || 'new') === 'new').length;
  const openTicketCount = tickets.filter(t => t.status === 'open').length;

  return (
    <div className="space-y-4">
      {/* Monthly summary */}
      {summary && (
        <div className="grid grid-cols-5 gap-2">
          <div className="pk-card p-3"><div className="text-[9px] tracking-widest uppercase text-neutral-500">Open ({summary.label})</div><div className="font-stat text-[22px]">{summary.buckets.open || 0}</div></div>
          <div className="pk-card p-3" style={{ background: '#e0f2fe' }}><div className="text-[9px] tracking-widest uppercase">Resolved by call</div><div className="font-stat text-[22px]">{summary.buckets.resolved_call || 0}</div></div>
          <div className="pk-card p-3" style={{ background: '#fef3c7' }}><div className="text-[9px] tracking-widest uppercase">Reprint</div><div className="font-stat text-[22px]">{summary.buckets.reprint || 0}</div></div>
          <div className="pk-card p-3" style={{ background: '#fee2e2' }}><div className="text-[9px] tracking-widest uppercase">Refund</div><div className="font-stat text-[22px]">{summary.buckets.refund || 0}</div></div>
          <div className="pk-card p-3"><div className="text-[9px] tracking-widest uppercase text-neutral-500">Total closed</div><div className="font-stat text-[22px]">{(summary.buckets.closed || 0) + (summary.buckets.resolved_call || 0) + (summary.buckets.reprint || 0) + (summary.buckets.refund || 0)}</div></div>
        </div>
      )}

      <div className="flex items-center gap-2">
        <button onClick={() => setSub('tickets')} className={`pk-chip ${sub === 'tickets' ? 'active' : ''} relative`} data-testid="sub-tickets">
          Customer / Partner tickets ({openTicketCount})
          {sub !== 'tickets' && openTicketCount > 0 && <span className="absolute -top-1 -right-1 w-2.5 h-2.5 rounded-full" style={{ background: 'var(--pk-primary)' }} />}
        </button>
        <button onClick={() => setSub('enterprise')} className={`pk-chip ${sub === 'enterprise' ? 'active' : ''} relative`} data-testid="sub-enterprise">
          Enterprise / Bulk inquiries ({inquiries.length})
          {sub !== 'enterprise' && newInquiryCount > 0 && <span className="absolute -top-1 -right-1 w-2.5 h-2.5 rounded-full" style={{ background: 'var(--pk-primary)' }} />}
        </button>
      </div>

      {sub === 'tickets' ? (
        <table className="w-full text-[12px]">
          <thead className="bg-neutral-100"><tr className="text-left"><th className="p-2 text-[9px] tracking-widest uppercase">Ticket</th><th className="p-2 text-[9px] tracking-widest uppercase">User</th><th className="p-2 text-[9px] tracking-widest uppercase">Type</th><th className="p-2 text-[9px] tracking-widest uppercase">Message</th><th className="p-2 text-[9px] tracking-widest uppercase">Status</th><th className="p-2 text-[9px] tracking-widest uppercase text-right">Actions</th></tr></thead>
          <tbody>
            {tickets.map(t => (
              <tr key={t.ticket_id} className="border-t border-neutral-200">
                <td className="p-2 font-mono text-[10px] uppercase">#{t.ticket_id.slice(-8)}<div className="text-neutral-400">{t.created_at?.slice(0,10)}</div></td>
                <td className="p-2">{t.user_name}<div className="text-[10px] text-neutral-500">{t.user_role} · {t.phone}</div></td>
                <td className="p-2">{t.type}</td>
                <td className="p-2 text-[11px] max-w-md">{t.message}{t.resolution_note && <div className="mt-1 text-[10px] italic text-neutral-500">Resolution: {t.resolution_note}</div>}</td>
                <td className="p-2">
                  <span className={`border border-black text-[9px] tracking-widest uppercase px-1.5 py-0.5 font-bold ${t.status === 'open' ? 'status-pending_partner' : 'status-delivered'}`}>{t.status}</span>
                  {t.resolution_type && <div className="text-[9px] text-neutral-500 uppercase tracking-widest mt-1">{t.resolution_type}</div>}
                </td>
                <td className="p-2 text-right">{t.status === 'open' && <>
                  <button onClick={() => { setResolveSel(t); setRf({ resolution_type: 'resolved_call', resolution_note: '' }); }} className="pk-chip text-[9px]" data-testid={`resolve-${t.ticket_id}`}><Check size={10}/> Resolve…</button>
                  <button onClick={() => quickClose(t.ticket_id)} className="pk-chip text-[9px] ml-1"><XIcon size={10}/> Close</button>
                </>}</td>
              </tr>
            ))}
            {tickets.length === 0 && <tr><td colSpan={6} className="p-6 text-center text-neutral-500">No tickets.</td></tr>}
          </tbody>
        </table>
      ) : (
        <div className="space-y-3">
          {/* Stage filter chips */}
          <div className="flex flex-wrap items-center gap-2">
            <div className="text-[9px] tracking-[0.24em] uppercase font-bold text-neutral-500 mr-1">Filter by stage:</div>
            {[{ id: 'all', label: 'All' }, ...ENTERPRISE_STAGES].map(s => {
              const active = stageFilter === s.id;
              const meta = s.id === 'all' ? { color: '#0a0a0a' } : stageMeta(s.id);
              return (
                <button
                  key={s.id}
                  onClick={() => setStageFilter(s.id)}
                  className={`pk-chip ${active ? 'active' : ''}`}
                  data-testid={`enq-filter-${s.id}`}
                >
                  <span className="w-1.5 h-1.5" style={{ background: meta.color }} />
                  {s.label} ({stageCount(s.id)})
                </button>
              );
            })}
          </div>

          <table className="w-full text-[12px]">
            <thead className="bg-neutral-100"><tr className="text-left">
              <th className="p-2 text-[9px] tracking-widest uppercase">Company</th>
              <th className="p-2 text-[9px] tracking-widest uppercase">Contact</th>
              <th className="p-2 text-[9px] tracking-widest uppercase">Volume</th>
              <th className="p-2 text-[9px] tracking-widest uppercase">Products</th>
              <th className="p-2 text-[9px] tracking-widest uppercase">Message</th>
              <th className="p-2 text-[9px] tracking-widest uppercase">Stage</th>
              <th className="p-2 text-[9px] tracking-widest uppercase">Received</th>
            </tr></thead>
            <tbody>
              {filteredInquiries.map(i => (
                <tr key={i.inquiry_id} className="border-t border-neutral-200">
                  <td className="p-2 font-bold">{i.company}</td>
                  <td className="p-2">{i.name}<div className="text-[10px] text-neutral-500 font-mono">{i.phone} · {i.email}</div></td>
                  <td className="p-2">{i.volume || '—'}</td>
                  <td className="p-2">{i.products || '—'}</td>
                  <td className="p-2 text-[11px] max-w-md">{i.message || '—'}</td>
                  <td className="p-2"><StageChip inquiry={i} onChange={(s) => changeStage(i.inquiry_id, s)} /></td>
                  <td className="p-2 font-mono text-[10px]">{i.created_at?.slice(0,16).replace('T', ' ')}</td>
                </tr>
              ))}
              {filteredInquiries.length === 0 && <tr><td colSpan={7} className="p-6 text-center text-neutral-500">No inquiries in this stage.</td></tr>}
            </tbody>
          </table>
        </div>
      )}

      {resolveSel && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={() => setResolveSel(null)}>
          <form onSubmit={resolve} className="bg-white pk-border-thick brutal-shadow-lg max-w-md w-full p-6 slide-up" onClick={e => e.stopPropagation()}>
            <div className="font-display text-[20px]">Resolve ticket</div>
            <div className="text-[11px] text-neutral-500 mt-1">#{resolveSel.ticket_id.slice(-8)} · {resolveSel.type}</div>
            <div className="mt-4 space-y-3">
              <div>
                <label className="pk-label">How was it resolved?</label>
                <div className="grid grid-cols-2 gap-2">
                  {[
                    ['resolved_call', 'Resolved by call'],
                    ['reprint', 'Reprint issued'],
                    ['refund', 'Refund issued'],
                    ['closed', 'Closed (no action)'],
                  ].map(([id, label]) => (
                    <button key={id} type="button" onClick={() => setRf({ ...rf, resolution_type: id })} className={`pk-chip justify-center ${rf.resolution_type === id ? 'active' : ''}`} data-testid={`res-${id}`}>{label}</button>
                  ))}
                </div>
              </div>
              <div><label className="pk-label">Note (optional)</label><textarea rows={2} className="pk-textarea" value={rf.resolution_note} onChange={e => setRf({ ...rf, resolution_note: e.target.value })} data-testid="res-note"/></div>
            </div>
            <div className="mt-4 flex gap-2">
              <button type="button" onClick={() => setResolveSel(null)} className="pk-btn pk-btn-outline flex-1">Cancel</button>
              <button type="submit" className="pk-btn pk-btn-magenta flex-1" data-testid="res-submit">Mark resolved</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

// ------------- Coupons Tab -------------
const emptyCoupon = () => ({
  code: '', description: '',
  discount_type: 'percent', discount_value: 10, max_discount_cap: null,
  min_order_amount: 0, starts_at: '', expires_at: '',
  total_usage_limit: null, per_user_limit: 1,
  applies_to: 'all', applies_to_ids: [],
  first_order_only: false, single_use_code: false, auto_apply_priority: 0, active: true,
});

const CouponsTab = () => {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null); // coupon or null
  const [viewing, setViewing] = useState(null); // { coupon, summary, rows } or null

  const load = async () => {
    try { const r = await API.get('/admin/coupons'); setItems(r.data); }
    catch { toast.error('Failed to load coupons'); }
  };
  useEffect(() => { load(); }, []);

  const openReport = async (id) => {
    try {
      const r = await API.get(`/admin/coupons/${id}/redemptions`);
      setViewing(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to load report'); }
  };

  const save = async () => {
    try {
      const payload = {
        ...editing,
        code: (editing.code || '').trim().toUpperCase(),
        discount_value: parseFloat(editing.discount_value) || 0,
        max_discount_cap: editing.max_discount_cap === '' || editing.max_discount_cap == null ? null : parseFloat(editing.max_discount_cap),
        min_order_amount: parseFloat(editing.min_order_amount) || 0,
        total_usage_limit: editing.total_usage_limit === '' || editing.total_usage_limit == null ? null : parseInt(editing.total_usage_limit, 10),
        per_user_limit: editing.per_user_limit === '' || editing.per_user_limit == null ? null : parseInt(editing.per_user_limit, 10),
        auto_apply_priority: parseInt(editing.auto_apply_priority, 10) || 0,
        starts_at: editing.starts_at ? new Date(editing.starts_at).toISOString() : null,
        expires_at: editing.expires_at ? new Date(editing.expires_at).toISOString() : null,
        applies_to_ids: (editing.applies_to_ids || []).map(s => s.trim()).filter(Boolean),
      };
      if (editing.coupon_id) await API.put(`/admin/coupons/${editing.coupon_id}`, payload);
      else await API.post('/admin/coupons', payload);
      toast.success('Saved');
      setEditing(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
  };

  const del = async (id) => {
    if (!confirm('Delete this coupon? Past redemption records remain.')) return;
    await API.delete(`/admin/coupons/${id}`); load();
  };

  const summary = (c) => c.discount_type === 'flat'
    ? `₹${c.discount_value} off`
    : `${c.discount_value}% off${c.max_discount_cap ? ` (max ₹${c.max_discount_cap})` : ''}`;

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <div className="text-[11px] text-neutral-500">{items.length} coupons · click Edit to change terms</div>
        <button onClick={() => setEditing(emptyCoupon())} className="pk-btn" data-testid="new-coupon"><Plus size={14}/> New Coupon</button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {items.map(c => (
          <div key={c.coupon_id} className={`pk-card p-4 ${!c.active ? 'opacity-60' : ''}`}>
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <span className="font-mono font-bold text-[15px] tracking-widest">{c.code}</span>
                  {!c.active && <span className="text-[9px] font-bold uppercase tracking-widest bg-neutral-200 px-1.5 py-0.5">inactive</span>}
                  {c.first_order_only && <span className="text-[9px] font-bold uppercase tracking-widest text-white bg-black px-1.5 py-0.5">1st-order</span>}
                  {c.single_use_code && <span className="text-[9px] font-bold uppercase tracking-widest text-white px-1.5 py-0.5" style={{background:'var(--pk-primary)'}}>single-use</span>}
                  {c.auto_apply_priority > 0 && <span className="text-[9px] font-bold uppercase tracking-widest text-black px-1.5 py-0.5" style={{background:'var(--pk-yellow)'}}>auto</span>}
                </div>
                <div className="text-[13px] font-bold mt-1">{summary(c)}</div>
                {c.description && <div className="text-[11px] text-neutral-600 mt-0.5">{c.description}</div>}
                <div className="text-[10px] text-neutral-500 mt-1 font-mono">
                  min ₹{c.min_order_amount || 0}
                  {c.per_user_limit != null && ` · ${c.per_user_limit}/user`}
                  {c.total_usage_limit != null && ` · ${c.redemptions || 0}/${c.total_usage_limit} used`}
                  {c.expires_at && ` · exp ${new Date(c.expires_at).toLocaleDateString()}`}
                </div>
              </div>
              <div className="flex flex-col gap-1 shrink-0">
                <button onClick={() => openReport(c.coupon_id)} className="pk-chip text-[9px]" data-testid={`report-coupon-${c.coupon_id}`}><Eye size={10}/> Report</button>
                <button onClick={() => setEditing({ ...c,
                  starts_at: c.starts_at ? c.starts_at.slice(0,16) : '',
                  expires_at: c.expires_at ? c.expires_at.slice(0,16) : '',
                })} className="pk-chip text-[9px]" data-testid={`edit-coupon-${c.coupon_id}`}><PencilSimple size={10}/> Edit</button>
                <button onClick={() => del(c.coupon_id)} className="pk-chip text-[9px]"><Trash size={10}/> Delete</button>
              </div>
            </div>
          </div>
        ))}
        {items.length === 0 && <div className="col-span-2 text-center text-[12px] text-neutral-500 py-8">No coupons yet — create your first one.</div>}
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={() => setEditing(null)}>
          <div className="bg-white pk-border-thick brutal-shadow-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto p-6" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between">
              <div className="font-display text-[22px]">{editing.coupon_id ? 'Edit coupon' : 'New coupon'}</div>
              <button onClick={() => setEditing(null)} className="pk-chip"><XIcon size={12}/></button>
            </div>

            <div className="mt-4 grid grid-cols-2 gap-3">
              <div>
                <label className="pk-label">Code</label>
                <input className="pk-input font-mono tracking-widest uppercase" placeholder="WELCOME10" value={editing.code} onChange={e => setEditing({...editing, code: e.target.value.toUpperCase()})} data-testid="c-code"/>
              </div>
              <div>
                <label className="pk-label">Active</label>
                <label className="flex items-center gap-2 h-11 pk-border px-3">
                  <input type="checkbox" checked={editing.active} onChange={e => setEditing({...editing, active: e.target.checked})}/>
                  <span className="text-[13px]">Coupon can be redeemed</span>
                </label>
              </div>
              <div className="col-span-2">
                <label className="pk-label">Description (shown to customer)</label>
                <input className="pk-input" placeholder="10% off your first order" value={editing.description || ''} onChange={e => setEditing({...editing, description: e.target.value})}/>
              </div>

              <div>
                <label className="pk-label">Discount type</label>
                <select className="pk-select" value={editing.discount_type} onChange={e => setEditing({...editing, discount_type: e.target.value})}>
                  <option value="percent">Percentage off (%)</option>
                  <option value="flat">Flat off (₹)</option>
                </select>
              </div>
              <div>
                <label className="pk-label">Discount value</label>
                <input type="number" step="any" className="pk-input font-mono" value={editing.discount_value} onChange={e => setEditing({...editing, discount_value: e.target.value})}/>
              </div>

              <div>
                <label className="pk-label">Max discount cap (₹) — percent only</label>
                <input type="number" className="pk-input font-mono" placeholder="Leave empty for no cap" value={editing.max_discount_cap ?? ''} onChange={e => setEditing({...editing, max_discount_cap: e.target.value})} disabled={editing.discount_type !== 'percent'}/>
              </div>
              <div>
                <label className="pk-label">Minimum order (₹)</label>
                <input type="number" className="pk-input font-mono" value={editing.min_order_amount} onChange={e => setEditing({...editing, min_order_amount: e.target.value})}/>
              </div>

              <div>
                <label className="pk-label">Starts at</label>
                <input type="datetime-local" className="pk-input font-mono" value={editing.starts_at || ''} onChange={e => setEditing({...editing, starts_at: e.target.value})}/>
              </div>
              <div>
                <label className="pk-label">Expires at</label>
                <input type="datetime-local" className="pk-input font-mono" value={editing.expires_at || ''} onChange={e => setEditing({...editing, expires_at: e.target.value})}/>
              </div>

              <div>
                <label className="pk-label">Total usage limit</label>
                <input type="number" className="pk-input font-mono" placeholder="Empty = unlimited" value={editing.total_usage_limit ?? ''} onChange={e => setEditing({...editing, total_usage_limit: e.target.value})}/>
              </div>
              <div>
                <label className="pk-label">Per user limit</label>
                <input type="number" className="pk-input font-mono" placeholder="Empty = unlimited" value={editing.per_user_limit ?? ''} onChange={e => setEditing({...editing, per_user_limit: e.target.value})}/>
              </div>

              <div>
                <label className="pk-label">Applies to</label>
                <select className="pk-select" value={editing.applies_to} onChange={e => setEditing({...editing, applies_to: e.target.value})}>
                  <option value="all">All products</option>
                  <option value="categories">Specific categories</option>
                  <option value="products">Specific products</option>
                </select>
              </div>
              <div>
                <label className="pk-label">Category / Product IDs (comma separated)</label>
                <input className="pk-input font-mono" placeholder="cat_paper, cat_large" disabled={editing.applies_to === 'all'} value={(editing.applies_to_ids || []).join(', ')} onChange={e => setEditing({...editing, applies_to_ids: e.target.value.split(',').map(s => s.trim())})}/>
              </div>

              <div>
                <label className="pk-label">Auto-apply priority</label>
                <input type="number" className="pk-input font-mono" value={editing.auto_apply_priority} onChange={e => setEditing({...editing, auto_apply_priority: e.target.value})}/>
                <div className="text-[10px] text-neutral-500 mt-1">Set &gt; 0 to make this coupon eligible for auto-apply at checkout.</div>
              </div>
              <div className="grid grid-rows-2 gap-2">
                <label className="flex items-center gap-2 pk-border px-3 py-2">
                  <input type="checkbox" checked={editing.first_order_only} onChange={e => setEditing({...editing, first_order_only: e.target.checked})}/>
                  <span className="text-[12px]">First-order only</span>
                </label>
                <label className="flex items-center gap-2 pk-border px-3 py-2">
                  <input type="checkbox" checked={editing.single_use_code} onChange={e => setEditing({...editing, single_use_code: e.target.checked})}/>
                  <span className="text-[12px]">Single-use code (invalidates after 1 redemption)</span>
                </label>
              </div>
            </div>

            <div className="mt-6 flex gap-2">
              <button onClick={() => setEditing(null)} className="pk-btn pk-btn-outline flex-1">Cancel</button>
              <button onClick={save} className="pk-btn pk-btn-magenta flex-1" data-testid="c-save">Save coupon</button>
            </div>
          </div>
        </div>
      )}

      {/* Redemption report modal */}
      {viewing && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={() => setViewing(null)}>
          <div className="bg-white pk-border-thick brutal-shadow-lg max-w-3xl w-full max-h-[90vh] overflow-y-auto p-6" onClick={e => e.stopPropagation()} data-testid="coupon-report">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-[10px] tracking-[0.24em] uppercase font-bold" style={{ color: 'var(--pk-primary)' }}>— Redemption Report</div>
                <div className="font-display text-[22px] mt-1">{viewing.coupon.code}</div>
                <div className="text-[11px] text-neutral-500 mt-0.5">{viewing.coupon.description || '—'}</div>
              </div>
              <button onClick={() => setViewing(null)} className="pk-chip"><XIcon size={12}/></button>
            </div>

            {/* Summary cards */}
            <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-2">
              <KPI label="Redemptions" value={viewing.summary.total_redemptions} />
              <KPI label="Paid orders" value={viewing.summary.paid_orders} />
              <KPI label="Total discount" value={`₹${(viewing.summary.total_discount_given || 0).toLocaleString('en-IN')}`} />
              <KPI label="Revenue (paid)" value={`₹${(viewing.summary.total_revenue_from_coupon || 0).toLocaleString('en-IN')}`} accent="var(--pk-yellow)" />
            </div>

            {/* Rows */}
            <div className="mt-5 space-y-2">
              {viewing.rows.length === 0 && (
                <div className="text-center text-[12px] text-neutral-500 py-8">No redemptions yet.</div>
              )}
              {viewing.rows.map((r) => (
                <div key={r.redemption_id} className="pk-card-flat p-3">
                  <div className="flex flex-wrap items-baseline justify-between gap-2">
                    <div className="min-w-0">
                      <div className="font-bold text-[13px]">{r.user_name || '—'}</div>
                      <div className="text-[10px] text-neutral-500 font-mono">{r.user_email} · {r.user_phone}</div>
                    </div>
                    <div className="text-right shrink-0">
                      <div className="text-[10px] tracking-widest uppercase text-neutral-500">Saved</div>
                      <div className="font-mono font-bold text-[14px]" style={{ color: 'var(--pk-primary)' }}>₹{(r.discount_amount || 0).toLocaleString('en-IN')}</div>
                    </div>
                  </div>
                  <div className="mt-2 flex flex-wrap items-center gap-2 text-[10px] tracking-widest uppercase">
                    <Link to={`/orders/${r.order_id}`} className="font-mono underline">{r.order_id}</Link>
                    {r.order_status && <span className={`px-1.5 py-0.5 status-${r.order_status}`}>{r.order_status.replace('_',' ')}</span>}
                    {r.payment_status && <span className="text-neutral-500">· {r.payment_status}</span>}
                    <span className="text-neutral-500 ml-auto font-mono normal-case tracking-normal">
                      subtotal ₹{(r.subtotal||0).toLocaleString('en-IN')} → total ₹{(r.order_total||0).toLocaleString('en-IN')}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};



export default AdminDashboard;
